import { supabase } from '@/lib/supabase';
import type {
  Tenant, TenantPlan, BrandingConfig, FeatureConfig,
  CreateTenantInput, UpdateTenantInput, UpdateBrandingInput,
  UpdateFeaturesInput, UsageMetric, AuditLog, PlatformStats,
  TenantConfigResponse, PlanName, TenantAdmin,
  AppTenantPricingPlan, UpdatePricingPlanInput,
  StripeInvoice, UpdateSeatsInput, ConnectOnboardingResult,
  ConnectAccountStatus, DowngradeInput,
  Announcement, CreateAnnouncementInput, UpdateAnnouncementInput,
  Bulletin, CreateBulletinInput, UpdateBulletinInput, BulletinInterest, BulletinInterestStatus,
  ShowcaseDestination, CreateShowcaseDestinationInput, UpdateShowcaseDestinationInput,
  BookingInquiry, BookingInquiryStatus, YouTubeVideoPreview,
  BusinessLead, BusinessLeadStatus,
  TenantReferralCode, TenantReferral, ReferralCommission, ReferralPayout,
  ReferralDashboardStats, PayoutStatus,
  TenantAffiliatePartner, AffiliateClickStats,
  TenantReferralConfig,
  PlatformEmailSettings, EmailTemplate,
  TenantEmailTemplate, UpsertTenantEmailTemplateInput,
  CampaignEmail, CreateCampaignInput, CampaignRecipient,
  PlatformAnnouncement, CreatePlatformAnnouncementInput, UpdatePlatformAnnouncementInput,
  DiscoveryInterview, CreateDiscoveryInterviewInput, UpdateDiscoveryInterviewInput,
  PlanFeatureDefault, CreatePlanInput,
} from '@/types';

// ── Tenants ──────────────────────────────────────────────────────────────────

/** PostgREST returns `numeric` columns as JSON strings to preserve full
 *  precision (numeric can hold values larger than JS Number can). For
 *  `tenants.storage_used_mb` (numeric(20,2)) this means math like
 *  `0 + storage_used_mb` would concatenate instead of add. Coerce here so
 *  every consumer can keep using it as `number`. */
function coerceTenantNumerics<T extends { storage_used_mb?: unknown } | null | undefined>(t: T): T {
  if (!t) return t;
  if ('storage_used_mb' in (t as object) && (t as { storage_used_mb?: unknown }).storage_used_mb != null) {
    (t as { storage_used_mb: number }).storage_used_mb = Number(
      (t as { storage_used_mb: unknown }).storage_used_mb,
    );
  }
  return t;
}

export async function fetchTenants(): Promise<Tenant[]> {
  const { data, error } = await supabase
    .from('tenants')
    .select(`*, plan:tenant_plans(*), branding:branding_configs(*), features:feature_configs(*)`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(coerceTenantNumerics);
}

export async function fetchTenant(id: string): Promise<Tenant> {
  const { data, error } = await supabase
    .from('tenants')
    .select(`*, plan:tenant_plans(*), branding:branding_configs(*), features:feature_configs(*)`)
    .eq('id', id)
    .single();
  if (error) throw error;
  return coerceTenantNumerics(data);
}

export async function createTenant(input: CreateTenantInput): Promise<Tenant> {
  const { data: plan, error: planErr } = await supabase
    .from('tenant_plans').select('*').eq('id', input.plan_id).single();
  if (planErr) throw planErr;

  const subdomain = `${input.slug}.discoverygem.com`;

  const { data: tenant, error: tErr } = await supabase
    .from('tenants')
    .insert({
      name: input.name,
      slug: input.slug,
      subdomain,
      status: 'active',
      plan_id: input.plan_id,
      owner_user_id: '00000000-0000-0000-0000-000000000000',
      max_users: plan.max_users || 5,
      storage_limit_mb: plan.max_storage_mb,
    })
    .select().single();
  if (tErr) throw tErr;

  // Create default branding — pre-seeded with a placeholder bio so brand-new
  // tenants have something readable in the navbar bio popover from minute one.
  // The owner can overwrite all of it from the Branding admin afterwards.
  const ownerFirstName = (input.owner_email?.trim() || '').split('@')[0].split(/[._-]/)[0];
  const ownerHello = ownerFirstName
    ? `Hi, I'm ${ownerFirstName.charAt(0).toUpperCase() + ownerFirstName.slice(1)}`
    : 'Welcome aboard';
  await supabase.from('branding_configs').insert({
    tenant_id: tenant.id,
    app_name: input.name,
    primary_color: input.branding?.primary_color || '#e54d2e',
    secondary_color: input.branding?.secondary_color || '#f47051',
    bio_heading: ownerHello,
    bio_text:
      "Welcome to my travel space. Here are the trips I love, the places I keep returning to, " +
      "and a few stories from the road. Pick any itinerary below — copy it into your account and " +
      "make it your own.",
  });

  // Create feature config from plan defaults (DB-driven)
  const defaults = await fetchPlanFeatureDefaultsMap(plan.id);
  await supabase.from('feature_configs').insert({ tenant_id: tenant.id, ...defaults });

  // Auto-seed 3 starter bulletins so the splash page isn't empty when the
  // owner first shares their live URL. Idempotent because we only run this
  // here at creation time. Owner can edit / delete freely afterwards.
  const STARTER_BULLETINS = [
    {
      tenant_id: tenant.id,
      title: 'Tuscany Foodie Trail · 7 days',
      description:
        'A curated week through Florence, Siena, and Chianti hill towns — wineries, family-run trattorias, and a Tuscan cooking class. Built for travellers who plan their day around dinner.',
      destinations: ['Florence', 'Siena', 'Chianti'],
      price_from: 2400,
      price_currency: 'USD',
      group_size_max: 8,
      image_url: 'https://images.unsplash.com/photo-1534445867742-43195f401b6c?w=1200',
      is_active: true,
    },
    {
      tenant_id: tenant.id,
      title: 'Tokyo + Kyoto Cherry Blossoms · 10 days',
      description:
        'Late-March itinerary timed to peak sakura. Quiet shrines at sunrise, Tsukiji breakfast, an overnight ryokan stay, and a slow afternoon under Maruyama Park lanterns.',
      destinations: ['Tokyo', 'Kyoto'],
      price_from: 3800,
      price_currency: 'USD',
      group_size_max: 12,
      image_url: 'https://images.unsplash.com/photo-1492571350019-22de08371fd3?w=1200',
      is_active: true,
    },
    {
      tenant_id: tenant.id,
      title: 'Bali Yoga & Reset · 5 days',
      description:
        'Ubud-based recharge with a daily sunrise vinyasa class, jungle cafes, a temple-water purification, and one optional surf morning in Canggu.',
      destinations: ['Ubud', 'Canggu'],
      price_from: 1450,
      price_currency: 'USD',
      group_size_max: 10,
      image_url: 'https://images.unsplash.com/photo-1518002171953-a080ee817e1f?w=1200',
      is_active: true,
    },
  ];
  await supabase.from('tenant_bulletins').insert(STARTER_BULLETINS).then(
    () => console.log('[api] seeded 3 starter bulletins for', tenant.id),
    (err: any) => console.warn('[api] starter bulletin seed failed:', err),
  );

  // Audit
  await logAudit(null, 'tenant_created', 'tenant', tenant.id, { name: input.name, plan: plan.name });

  // Provision B2C pricing in the App DB (awaited so errors surface)
  try {
    await provisionTenantInAppDb(tenant.id, input.name);
  } catch (err) {
    console.error('[api] provision-tenant FAILED — B2C plans were NOT seeded:', err);
    // Don't throw — tenant was already created. Flag it for manual re-provisioning.
    await supabase.from('tenants').update({ provisioning_status: 'failed' }).eq('id', tenant.id).then(
      () => {},
      () => {},  // ignore if column doesn't exist yet
    );
  }

  // Provision subdomain (Cloudflare DNS + Netlify domain alias) — fire-and-forget
  provisionSubdomain(input.slug).catch(err =>
    console.warn('[api] provision-subdomain fire-and-forget failed:', err)
  );

  // Auto-generate referral code for the new tenant
  const refCode = `${input.slug.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  supabase.from('tenant_referral_codes').insert({ tenant_id: tenant.id, referral_code: refCode }).then(
    () => console.log('[api] referral code created:', refCode),
    (err: any) => console.warn('[api] referral code creation failed:', err),
  );

  // Invite owner as tenant admin (awaited so errors are visible)
  if (input.owner_email?.trim()) {
    console.log('[api] createTenant — sending owner invite to:', input.owner_email.trim());
    try {
      const inviteRes = await inviteTenantAdminViaEdge(tenant.id, input.owner_email.trim(), 'owner', input.name);
      console.log('[api] owner invite success:', inviteRes);
    } catch (inviteErr: any) {
      console.error('[api] owner invite FAILED:', inviteErr?.message || inviteErr);
      // Don't throw — tenant is already created, but surface the error in console
    }
  }

  return fetchTenant(tenant.id);
}

export async function updateTenant(id: string, input: UpdateTenantInput): Promise<Tenant> {
  const { error } = await supabase.from('tenants')
    .update({ ...input, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
  await logAudit(null, 'tenant_updated', 'tenant', id, input as Record<string, unknown>);
  return fetchTenant(id);
}

export async function suspendTenant(id: string): Promise<void> {
  const { error } = await supabase.from('tenants')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
  await logAudit(null, 'tenant_suspended', 'tenant', id, {});
}

// ── Plans ────────────────────────────────────────────────────────────────────

/** Fetch ALL plans (active + archived), ordered by rank. Used by Plan Builder. */
export async function fetchPlans(): Promise<TenantPlan[]> {
  const { data, error } = await supabase
    .from('tenant_plans')
    .select('*')
    .order('rank', { ascending: true });
  if (error) throw error;
  return data || [];
}

/** Fetch only active plans, ordered by rank. Used by Billing, Features, ProvisionWizard. */
export async function fetchActivePlans(): Promise<TenantPlan[]> {
  const { data, error } = await supabase
    .from('tenant_plans')
    .select('*')
    .eq('is_active', true)
    .order('rank', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ── Branding ─────────────────────────────────────────────────────────────────

export async function updateBranding(tenantId: string, input: UpdateBrandingInput): Promise<BrandingConfig> {
  const { data, error } = await supabase.from('branding_configs')
    .update(input).eq('tenant_id', tenantId).select().single();
  if (error) throw error;
  return data;
}

// ── Features ─────────────────────────────────────────────────────────────────

export async function updateFeatures(tenantId: string, input: UpdateFeaturesInput): Promise<FeatureConfig> {
  const { data, error } = await supabase.from('feature_configs')
    .update(input).eq('tenant_id', tenantId).select().single();
  if (error) throw error;
  return data;
}

// ── Tenant Admins ───────────────────────────────────────────────────────────

export async function fetchTenantAdmins(tenantId: string): Promise<TenantAdmin[]> {
  const { data, error } = await supabase
    .from('tenant_admins')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('invited_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ─────────────────────────────────────────────────────────────────────────────
// Tenant admin sign-in activity — surfaces auth.users.last_sign_in_at on the
// super-admin UI so the operator can see which admins actually use their
// accounts vs the ones who got invited and ghosted.
//
// Both helpers below call SECURITY DEFINER RPCs that gate on
// is_platform_admin() server-side; tenant admins and end-users get a 42501
// from the database. Status is computed in SQL so the UI never has to
// re-implement the bucketing rules. See migration
// 20260526000000_tenant_admin_signin_activity.sql for the SQL.
// ─────────────────────────────────────────────────────────────────────────────

/** One row per (tenant_admin, tenant) entry — used on the per-tenant
 *  TenantDetail page. */
export interface TenantAdminSigninActivity {
  tenant_admin_id:  string;
  user_id:          string | null;
  email:            string | null;
  role:             string;             // 'owner' | 'admin' | 'manager'
  invited_at:       string;
  accepted_at:      string | null;
  last_sign_in_at:  string | null;
  status:           'pending' | 'never_signed_in' | 'active' | 'stale' | 'inactive';
}

export async function fetchTenantAdminSigninActivity(
  tenantId: string,
): Promise<TenantAdminSigninActivity[]> {
  const { data, error } = await supabase.rpc('get_tenant_admin_signin_activity', {
    p_tenant_id: tenantId,
  });
  if (error) throw new Error(error.message || error.code || 'Failed to load admin sign-in activity');
  return (data || []) as TenantAdminSigninActivity[];
}

/** Aggregate counts across ALL tenants — used by the Tenants list page to
 *  show an "Admin activity" column without N+1ing one query per tenant. */
export interface TenantAdminActivitySummary {
  tenant_id:               string;
  admins_total:            number;
  admins_pending:          number;
  admins_never_signed_in:  number;
  admins_active:           number;
  admins_stale:            number;
  admins_inactive:         number;
  most_recent_admin_signin: string | null;
}

export async function fetchTenantsAdminActivity(): Promise<TenantAdminActivitySummary[]> {
  const { data, error } = await supabase.rpc('get_tenants_with_admin_activity');
  if (error) throw new Error(error.message || error.code || 'Failed to load tenants admin activity');
  return (data || []) as TenantAdminActivitySummary[];
}

/** Auto-accept any pending tenant admin invites for the current user. */
async function acceptPendingInvites(): Promise<void> {
  const { error } = await supabase.rpc('accept_my_pending_invites');
  if (error) console.warn('[acceptPendingInvites] failed:', error.message);
}

export async function fetchMyTenants(): Promise<{ tenant: Tenant; role: string }[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  // Auto-accept pending invites on login (idempotent, non-fatal)
  await acceptPendingInvites();

  const { data, error } = await supabase
    .from('tenant_admins')
    .select('role, tenant:tenants(*, plan:tenant_plans(*), branding:branding_configs(*), features:feature_configs(*))')
    .eq('user_id', user.id)
    .not('accepted_at', 'is', null);
  if (error) throw error;

  return (data || [])
    .filter((row: any) => row.tenant)
    .map((row: any) => ({ tenant: row.tenant as Tenant, role: row.role as string }));
}

export async function inviteTenantAdmin(
  tenantId: string,
  email: string,
  role: 'owner' | 'admin' | 'manager',
): Promise<TenantAdmin> {
  const { data, error } = await supabase
    .from('tenant_admins')
    .insert({
      tenant_id: tenantId,
      user_id: '00000000-0000-0000-0000-000000000000', // placeholder until user accepts
      email,
      role,
      invited_at: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function inviteTenantAdminViaEdge(
  tenantId: string,
  email: string,
  role: 'owner' | 'admin' | 'manager',
  tenantName: string,
): Promise<{ success: boolean; user_id: string; admin_id: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/invite-tenant-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      email,
      role,
      tenant_name: tenantName,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to invite admin');
  return json;
}

export async function removeTenantAdmin(adminId: string): Promise<void> {
  const { error } = await supabase
    .from('tenant_admins')
    .delete()
    .eq('id', adminId);
  if (error) throw error;
}

// ── Announcements ──────────────────────────────────────────────────────────

export async function fetchAnnouncements(tenantId: string): Promise<Announcement[]> {
  const { data, error } = await supabase
    .from('announcements')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createAnnouncement(
  tenantId: string,
  input: CreateAnnouncementInput,
): Promise<Announcement> {
  // If activating, deactivate any existing active announcement first
  if (input.is_active) {
    await supabase
      .from('announcements')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('tenant_id', tenantId)
      .eq('is_active', true);
  }

  const { data, error } = await supabase
    .from('announcements')
    .insert({ tenant_id: tenantId, ...input })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateAnnouncement(
  announcementId: string,
  tenantId: string,
  input: UpdateAnnouncementInput,
): Promise<Announcement> {
  // If activating, deactivate any other active announcement first
  if (input.is_active) {
    await supabase
      .from('announcements')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .neq('id', announcementId);
  }

  const { data, error } = await supabase
    .from('announcements')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', announcementId)
    .eq('tenant_id', tenantId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteAnnouncement(
  announcementId: string,
  tenantId: string,
): Promise<void> {
  const { error } = await supabase
    .from('announcements')
    .delete()
    .eq('id', announcementId)
    .eq('tenant_id', tenantId);
  if (error) throw error;
}

// ── Bulletins (Agency Bulletin Board) ────────────────────────────────────────

export async function fetchBulletins(tenantId: string): Promise<Bulletin[]> {
  const { data, error } = await supabase
    .from('tenant_bulletins')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createBulletin(
  tenantId: string,
  input: CreateBulletinInput,
): Promise<Bulletin> {
  const { data, error } = await supabase
    .from('tenant_bulletins')
    .insert({ tenant_id: tenantId, ...input })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateBulletin(
  bulletinId: string,
  tenantId: string,
  input: UpdateBulletinInput,
): Promise<Bulletin> {
  const { data, error } = await supabase
    .from('tenant_bulletins')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', bulletinId)
    .eq('tenant_id', tenantId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBulletin(
  bulletinId: string,
  tenantId: string,
): Promise<void> {
  const { error } = await supabase
    .from('tenant_bulletins')
    .delete()
    .eq('id', bulletinId)
    .eq('tenant_id', tenantId);
  if (error) throw error;
}

export async function fetchBulletinInterests(
  bulletinId: string,
): Promise<BulletinInterest[]> {
  const { data, error } = await supabase
    .from('bulletin_interests')
    .select('*')
    .eq('bulletin_id', bulletinId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Fetch every interest across all of a tenant's bulletins, joined with the
 * bulletin title so the admin sees which listing each lead came from.
 * Backs the "Interested" tab on the Bulletin Board.
 */
export async function fetchAllBulletinInterests(
  tenantId: string,
  filter?: BulletinInterestStatus | 'all',
): Promise<BulletinInterest[]> {
  // Pull this tenant's bulletin IDs first — RLS would also enforce this, but
  // we need the title for each row and joining keeps it to one round trip.
  const { data: bulletins, error: bErr } = await supabase
    .from('tenant_bulletins')
    .select('id, title')
    .eq('tenant_id', tenantId);
  if (bErr) throw bErr;
  if (!bulletins || bulletins.length === 0) return [];

  const titleById = new Map(bulletins.map(b => [b.id, b.title as string]));

  let q = supabase
    .from('bulletin_interests')
    .select('*')
    .in('bulletin_id', bulletins.map(b => b.id))
    .order('created_at', { ascending: false });

  if (filter && filter !== 'all') {
    q = q.eq('status', filter);
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(row => ({
    ...row,
    bulletin_title: titleById.get(row.bulletin_id),
  }));
}

/**
 * Update a single bulletin interest's status and/or admin notes.
 * Tenant-admin-only via RLS.
 */
export async function updateBulletinInterest(
  interestId: string,
  patch: { status?: BulletinInterestStatus; admin_notes?: string | null },
): Promise<BulletinInterest> {
  const { data, error } = await supabase
    .from('bulletin_interests')
    .update(patch)
    .eq('id', interestId)
    .select('*')
    .single();
  if (error) throw error;
  return data as BulletinInterest;
}

export interface BulletinInterestStats {
  total_interests: number;
  recent_interests: number; // last 7 days
  active_bulletins: number;
  top_bulletin_title: string | null;
  top_bulletin_interests: number;
}

export async function fetchBulletinInterestStats(
  tenantId: string,
): Promise<BulletinInterestStats> {
  // Fetch all bulletins with their interest counts
  const { data: bulletins, error: bErr } = await supabase
    .from('tenant_bulletins')
    .select('id, title, is_active')
    .eq('tenant_id', tenantId);
  if (bErr) throw bErr;

  if (!bulletins || bulletins.length === 0) {
    return { total_interests: 0, recent_interests: 0, active_bulletins: 0, top_bulletin_title: null, top_bulletin_interests: 0 };
  }

  const bulletinIds = bulletins.map(b => b.id);
  const activeBulletins = bulletins.filter(b => b.is_active).length;

  // Fetch all interests for this tenant's bulletins
  const { data: allInterests, error: iErr } = await supabase
    .from('bulletin_interests')
    .select('id, bulletin_id, created_at')
    .in('bulletin_id', bulletinIds);
  if (iErr) throw iErr;

  const interests = allInterests || [];
  const total = interests.length;

  // Recent = last 7 days
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const recent = interests.filter(i => new Date(i.created_at) >= weekAgo).length;

  // Find top bulletin
  const countMap: Record<string, number> = {};
  interests.forEach(i => { countMap[i.bulletin_id] = (countMap[i.bulletin_id] || 0) + 1; });

  let topId = '';
  let topCount = 0;
  for (const [id, count] of Object.entries(countMap)) {
    if (count > topCount) { topId = id; topCount = count; }
  }

  const topBulletin = bulletins.find(b => b.id === topId);

  return {
    total_interests: total,
    recent_interests: recent,
    active_bulletins: activeBulletins,
    top_bulletin_title: topBulletin?.title || null,
    top_bulletin_interests: topCount,
  };
}

// ── Showcase Destinations ────────────────────────────────────────────────────

export async function fetchShowcaseDestinations(
  tenantId: string,
): Promise<ShowcaseDestination[]> {
  const { data, error } = await supabase
    .from('showcase_destinations')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createShowcaseDestination(
  tenantId: string,
  input: CreateShowcaseDestinationInput,
): Promise<ShowcaseDestination> {
  const { data, error } = await supabase
    .from('showcase_destinations')
    .insert({ tenant_id: tenantId, ...input })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateShowcaseDestination(
  destinationId: string,
  tenantId: string,
  input: UpdateShowcaseDestinationInput,
): Promise<ShowcaseDestination> {
  const { data, error } = await supabase
    .from('showcase_destinations')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', destinationId)
    .eq('tenant_id', tenantId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteShowcaseDestination(
  destinationId: string,
  tenantId: string,
): Promise<void> {
  const { error } = await supabase
    .from('showcase_destinations')
    .delete()
    .eq('id', destinationId)
    .eq('tenant_id', tenantId);
  if (error) throw error;
}

export async function reorderShowcaseDestinations(
  tenantId: string,
  orderedIds: string[],
): Promise<void> {
  const updates = orderedIds.map((id, index) =>
    supabase
      .from('showcase_destinations')
      .update({ sort_order: index, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('tenant_id', tenantId)
  );
  const results = await Promise.all(updates);
  const failed = results.find(r => r.error);
  if (failed?.error) throw failed.error;
}

// ── Booking Inquiries ────────────────────────────────────────────────────────

export async function fetchBookingInquiries(
  tenantId: string,
  status?: BookingInquiryStatus,
): Promise<BookingInquiry[]> {
  let q = supabase
    .from('booking_inquiries')
    .select('*, bulletin:tenant_bulletins(title)')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(200);

  if (status) q = q.eq('status', status);

  const { data, error } = await q;
  if (error) throw error;

  return (data || []).map((d: any) => ({
    ...d,
    bulletin_title: d.bulletin?.title || null,
  }));
}

export async function updateBookingInquiryStatus(
  inquiryId: string,
  tenantId: string,
  status: BookingInquiryStatus,
  adminNotes?: string,
): Promise<BookingInquiry> {
  const update: Record<string, any> = {
    status,
    updated_at: new Date().toISOString(),
  };
  if (adminNotes !== undefined) update.admin_notes = adminNotes;

  const { data, error } = await supabase
    .from('booking_inquiries')
    .update(update)
    .eq('id', inquiryId)
    .eq('tenant_id', tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ── YouTube Import ──────────────────────────────────────────────────────────

export async function fetchYouTubeVideos(
  url: string,
  tenantId: string,
): Promise<{ videos: YouTubeVideoPreview[]; remaining_quota: number; max_allowed: number }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/youtube-import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ url, tenant_id: tenantId }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to fetch YouTube videos');
  return { videos: json.videos ?? [], remaining_quota: json.remaining_quota ?? 0, max_allowed: json.max_allowed ?? 20 };
}

// ── Tenant Domains ──────────────────────────────────────────────────────────

export async function fetchDomain(tenantId: string): Promise<{ id: string; domain: string; verification_token: string; verified_at: string | null; ssl_status: string; cloudflare_hostname_id?: string } | null> {
  const { data, error } = await supabase
    .from('tenant_domains')
    .select('*')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function saveDomain(tenantId: string, domain: string): Promise<{ id: string; domain: string; verification_token: string }> {
  // Generate a verification token
  const token = crypto.randomUUID().replace(/-/g, '').slice(0, 16);

  // Upsert — if a domain record exists for this tenant, update it
  const { data: existing } = await supabase
    .from('tenant_domains')
    .select('id')
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from('tenant_domains')
      .update({ domain, verification_token: token, verified_at: null, ssl_status: 'pending' })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('tenant_domains')
      .insert({ tenant_id: tenantId, domain, verification_token: token })
      .select()
      .single();
    if (error) throw error;
    return data;
  }
}

export async function verifyDomain(tenantId: string, domain: string): Promise<{ verified: boolean; message?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/verify-domain`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ tenant_id: tenantId, domain }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to verify domain');
  return json;
}

export async function checkDomainSSL(tenantId: string, domain: string): Promise<{
  ssl_status: string; message?: string;
  cloudflare_ssl_status?: string; cloudflare_hostname_status?: string;
}> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/check-domain-ssl`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ tenant_id: tenantId, domain }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to check SSL status');
  return json;
}

/**
 * Remove custom domain: deletes Cloudflare Custom Hostname, tenant_domains record,
 * and clears custom_domain on the tenants table.
 */
export async function removeDomain(tenantId: string): Promise<{ domain: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/remove-domain`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ tenant_id: tenantId }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to remove domain');
  return json;
}

// ── Usage Metrics ────────────────────────────────────────────────────────────

/** Same numeric() string-coercion as coerceTenantNumerics, but for the
 *  rows on usage_metrics. */
function coerceUsageMetricNumerics(m: UsageMetric): UsageMetric {
  if (m && m.storage_used_mb != null) {
    (m as unknown as { storage_used_mb: number }).storage_used_mb = Number(m.storage_used_mb);
  }
  return m;
}

export async function fetchUsageMetrics(
  tenantId: string,
  rangeOrDays: number | { from: string; to: string } = 30,
): Promise<UsageMetric[]> {
  let from: string;
  let to: string | null = null;
  if (typeof rangeOrDays === 'number') {
    from = new Date(Date.now() - rangeOrDays * 86400000).toISOString().split('T')[0];
  } else {
    from = rangeOrDays.from;
    to   = rangeOrDays.to;
  }
  let q = supabase.from('usage_metrics')
    .select('*').eq('tenant_id', tenantId).gte('date', from);
  if (to) q = q.lte('date', to);
  const { data, error } = await q.order('date', { ascending: true });
  if (error) throw error;
  return (data || []).map(coerceUsageMetricNumerics);
}

/** Fetch usage metrics for ALL tenants (super admin analytics) */
export async function fetchAllUsageMetrics(days = 30): Promise<UsageMetric[]> {
  const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
  const { data, error } = await supabase.from('usage_metrics')
    .select('*').gte('date', from)
    .order('date', { ascending: true });
  if (error) throw error;
  return (data || []).map(coerceUsageMetricNumerics);
}

// ── Per-tenant cost panel ────────────────────────────────────────────
// Drives the cost column on Tenants list + drill-down on TenantDetail.
// Backend is list-tenant-costs Platform edge fn which proxies to the
// App's tenant-costs fn + joins tenant names from this DB.
// All money fields are integer US cents.

export interface TenantCostRow {
  tenant_id:         string;
  tenant_name:       string;
  tenant_slug:       string | null;
  plan_id:           string | null;
  llm_cents:         number;
  llm_request_count: number;
  image_cents:       number;
  video_cents:       number;
  audio_cents:       number;
  other_3p_cents:    number;
  total_cents:       number;
  prompt_tokens:     number;
  completion_tokens: number;
}

/** One row per feature (ai_requests.feature) for a tenant — the "which
 *  surface burns the tokens" breakdown. LLM-only. */
export interface TenantFeatureCostRow {
  feature:           string;
  request_count:     number;
  prompt_tokens:     number;
  completion_tokens: number;
  cost_cents:        number;
}

export interface TenantCostDailyRow {
  bucket_date: string;
  llm_cents:   number;
  image_cents: number;
  video_cents: number;
  audio_cents: number;
  other_cents: number;
  total_cents: number;
}

function rangeFromDays(days: number): { start_at: string; end_at: string } {
  const end = new Date();
  const start = new Date(end.getTime() - days * 86400000);
  return { start_at: start.toISOString(), end_at: end.toISOString() };
}

/** Rollup for every tenant over the last N days (default 30). */
export async function fetchTenantCostsRollup(days = 30): Promise<TenantCostRow[]> {
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: { mode: 'rollup', ...rangeFromDays(days) },
  });
  if (error) throw error;
  return (data?.rollup ?? []) as TenantCostRow[];
}

// ─── Vendor runway (cost board) ──────────────────────────────────────
export interface VendorAccount {
  vendor_key:             string;
  display_name:           string;
  category:               string;
  billing_shape:          'metered' | 'prepaid' | 'subscription' | 'sandbox' | 'build';
  currency:               string;
  balance_cents:          number | null;
  credit_unit:            string | null;
  credits_balance:        number | null;
  credit_to_usd_cents:    number | null;
  monthly_budget_cents:   number | null;
  monthly_amount_cents:   number | null;
  renewal_day:            number | null;
  is_tenant_attributable: boolean;
  low_runway_alert_days:  number;
  is_active:              boolean;
  sort_order:             number;
  notes:                  string | null;
}

export interface VendorLedgerEntry {
  id:           string;
  vendor_key:   string;
  entry_type:   'topup' | 'invoice' | 'adjustment' | 'subscription';
  amount_cents: number;
  credits:      number | null;
  occurred_at:  string;
  note:         string | null;
}

export interface VendorRollupRow {
  vendor:         string;
  service:        string;
  cost_cents:     number;
  event_count:    number;
  unpriced_count: number;
  units:          number;
}

export interface TenantVendorRow {
  tenant_id:   string;
  tenant_name: string;
  tenant_slug: string | null;
  plan_id:     string | null;
  vendor:      string;
  cost_cents:  number;
  event_count: number;
}

/** Vendor account config + balances (Platform DB, super-admin). */
export async function listVendorAccounts(): Promise<VendorAccount[]> {
  const { data, error } = await supabase.from('vendor_accounts').select('*').order('sort_order');
  if (error) throw error;
  return (data ?? []) as VendorAccount[];
}

export async function upsertVendorAccount(row: Partial<VendorAccount> & { vendor_key: string }): Promise<void> {
  const { error } = await supabase.from('vendor_accounts').upsert(row, { onConflict: 'vendor_key' });
  if (error) throw error;
}

/** Patch an EXISTING vendor account (no insert) — avoids the NOT-NULL trip that
 *  upsert hits when only a couple of columns are sent for a row that exists. */
export async function updateVendorAccount(vendorKey: string, patch: Partial<VendorAccount>): Promise<void> {
  const { error } = await supabase.from('vendor_accounts').update(patch).eq('vendor_key', vendorKey);
  if (error) throw error;
}

export async function listVendorLedger(vendorKey?: string): Promise<VendorLedgerEntry[]> {
  let q = supabase.from('vendor_ledger').select('*').order('occurred_at', { ascending: false });
  if (vendorKey) q = q.eq('vendor_key', vendorKey);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as VendorLedgerEntry[];
}

/** Record a top-up / invoice / adjustment (the "what I actually paid" ledger). */
export async function recordVendorPurchase(entry: {
  vendor_key:   string;
  amount_cents: number;
  credits?:     number | null;
  entry_type?:  'topup' | 'invoice' | 'adjustment' | 'subscription';
  occurred_at?: string;
  note?:        string | null;
}): Promise<void> {
  const { error } = await supabase.from('vendor_ledger').insert({
    vendor_key:   entry.vendor_key,
    amount_cents: entry.amount_cents,
    credits:      entry.credits ?? null,
    entry_type:   entry.entry_type ?? 'topup',
    occurred_at:  entry.occurred_at ?? new Date().toISOString(),
    note:         entry.note ?? null,
  });
  if (error) throw error;
}

/** Per-vendor consumption across all tenants for a date range (ISO). */
export async function fetchVendorRollup(startIso: string, endIso: string): Promise<VendorRollupRow[]> {
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: { mode: 'vendor_rollup', start_at: startIso, end_at: endIso },
  });
  if (error) throw error;
  return (data?.vendors ?? []) as VendorRollupRow[];
}

/** Tenant × vendor consumption matrix for a date range (ISO). */
export async function fetchTenantVendorMatrix(startIso: string, endIso: string): Promise<TenantVendorRow[]> {
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: { mode: 'tenant_vendor', start_at: startIso, end_at: endIso },
  });
  if (error) throw error;
  return (data?.rows ?? []) as TenantVendorRow[];
}

/** Per-tenant MONTHLY revenue in cents (plan price/seat × active seats) —
 *  the cost-to-serve denominator for the margin column. */
export async function fetchTenantRevenueMap(): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('tenants')
    .select('id, current_user_count, plan:tenant_plans(price_per_seat_monthly)');
  if (error) throw error;
  const m: Record<string, number> = {};
  for (const t of (data ?? []) as Array<{ id: string; current_user_count: number | null; plan: unknown }>) {
    const planObj = Array.isArray(t.plan) ? t.plan[0] : t.plan;
    const seat = (planObj as { price_per_seat_monthly?: number } | null)?.price_per_seat_monthly ?? 0;
    m[t.id] = seat * (t.current_user_count ?? 0);
  }
  return m;
}

/** Daily series for one tenant over the last N days (default 30). */
export async function fetchTenantCostsDaily(
  tenantId: string,
  days = 30,
): Promise<TenantCostDailyRow[]> {
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: { mode: 'daily', tenant_id: tenantId, ...rangeFromDays(days) },
  });
  if (error) throw error;
  return (data?.daily ?? []) as TenantCostDailyRow[];
}

export interface TenantCostMonthlyRow {
  bucket_month: string;
  llm_cents:    number;
  image_cents:  number;
  video_cents:  number;
  audio_cents:  number;
  other_cents:  number;
  total_cents:  number;
}

/** N-month series for one tenant (default 12 — trailing year). */
export async function fetchTenantCostsMonthly(
  tenantId: string,
  monthsBack = 12,
): Promise<TenantCostMonthlyRow[]> {
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: { mode: 'monthly', tenant_id: tenantId, months_back: monthsBack },
  });
  if (error) throw error;
  return (data?.monthly ?? []) as TenantCostMonthlyRow[];
}

export interface TenantCostLineItem {
  occurred_at:        string;
  source:             'ai_request' | 'third_party_charge';
  user_id:            string | null;
  user_email:         string | null;
  provider:           string;
  service_or_feature: string;
  model:              string | null;
  units:              number | null;
  cost_usd_cents:     number | null;
  ref_table:          string | null;
  ref_id:             string | null;
}

/** Paginated line-item drill-down for one tenant.
 *  Answers "who did what" inside the picked window. */
export async function fetchTenantCostLineItems(
  tenantId: string,
  opts: { days?: number; limit?: number; offset?: number } = {},
): Promise<{ items: TenantCostLineItem[]; total_count: number }> {
  const { days = 30, limit = 50, offset = 0 } = opts;
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: {
      mode: 'line_items',
      tenant_id: tenantId,
      ...rangeFromDays(days),
      limit,
      offset,
    },
  });
  if (error) throw error;
  return {
    items:       (data?.items ?? []) as TenantCostLineItem[],
    total_count: Number(data?.total_count ?? 0),
  };
}

/** Per-feature LLM token + cost breakdown for one tenant over the window. */
export async function fetchTenantFeatureCosts(
  tenantId: string,
  days = 30,
): Promise<TenantFeatureCostRow[]> {
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: { mode: 'feature_costs', tenant_id: tenantId, ...rangeFromDays(days) },
  });
  if (error) throw error;
  return (data?.features ?? []) as TenantFeatureCostRow[];
}

/** Per-feature LLM breakdown across ALL tenants (super-admin platform view). */
export interface PlatformFeatureCostRow {
  feature:           string;
  tenant_count:      number;
  request_count:     number;
  prompt_tokens:     number;
  completion_tokens: number;
  cost_cents:        number;
}

export async function fetchPlatformFeatureCosts(days = 30): Promise<PlatformFeatureCostRow[]> {
  const { data, error } = await supabase.functions.invoke('list-tenant-costs', {
    body: { mode: 'platform_feature_costs', ...rangeFromDays(days) },
  });
  if (error) throw error;
  return (data?.features ?? []) as PlatformFeatureCostRow[];
}

// ── ai_model_pricing (Pricing editor in AISettings) ──────────────────

export interface AiModelPricingRow {
  id:                       string;
  provider:                 string;
  model:                    string;
  kind:                     'llm' | 'image' | 'video' | 'audio' | 'render' | 'call' | 'fee_bps';
  input_per_1m_usd_cents:   number | null;
  output_per_1m_usd_cents:  number | null;
  per_unit_cents:           number | null;
  flat_fee_cents:           number | null;
  fee_bps:                  number | null;
  notes:                    string | null;
  is_active:                boolean;
  updated_at:               string;
}

export async function fetchAiModelPricing(): Promise<AiModelPricingRow[]> {
  const { data, error } = await supabase
    .from('ai_model_pricing')
    .select('*')
    .order('kind', { ascending: true })
    .order('provider', { ascending: true })
    .order('model', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AiModelPricingRow[];
}

export async function updateAiModelPricing(
  id: string,
  patch: Partial<Omit<AiModelPricingRow, 'id' | 'updated_at'>>,
): Promise<AiModelPricingRow> {
  const { data, error } = await supabase
    .from('ai_model_pricing')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as AiModelPricingRow;
}

export async function createAiModelPricing(
  row: Omit<AiModelPricingRow, 'id' | 'updated_at'>,
): Promise<AiModelPricingRow> {
  const { data, error } = await supabase
    .from('ai_model_pricing')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as AiModelPricingRow;
}

export async function deleteAiModelPricing(id: string): Promise<void> {
  const { error } = await supabase.from('ai_model_pricing').delete().eq('id', id);
  if (error) throw error;
}

// ── Platform Stats ───────────────────────────────────────────────────────────

export async function fetchPlatformStats(): Promise<PlatformStats> {
  const { data: tenants, error } = await supabase
    .from('tenants').select('*, plan:tenant_plans(*)');
  if (error) throw error;

  const all = tenants || [];
  const active = all.filter(t => t.status === 'active');
  const trial = all.filter(t => t.status === 'trial');
  const totalUsers = all.reduce((s, t) => s + (t.current_user_count || 0), 0);
  const mrr = active.reduce((s, t) => {
    const p = t.plan as TenantPlan;
    return s + (p ? p.price_per_seat_monthly * t.current_user_count : 0);
  }, 0);

  // ── Real metrics from usage_metrics table ──────────────────────────────
  const monthStart = new Date();
  monthStart.setDate(1);
  const monthStartStr = monthStart.toISOString().split('T')[0];

  const { data: monthMetrics } = await supabase
    .from('usage_metrics')
    .select('trips_created, ai_requests')
    .gte('date', monthStartStr);

  const tripsThisMonth = (monthMetrics || []).reduce((s, m) => s + (m.trips_created || 0), 0);
  const aiRequestsThisMonth = (monthMetrics || []).reduce((s, m) => s + (m.ai_requests || 0), 0);

  // ── Growth rate: compare this month's users to last month's ────────────
  const lastMonthStart = new Date();
  lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);
  lastMonthStart.setDate(1);
  const lastMonthEnd = new Date();
  lastMonthEnd.setDate(0); // last day of previous month
  const lastMonthStartStr = lastMonthStart.toISOString().split('T')[0];
  const lastMonthEndStr = lastMonthEnd.toISOString().split('T')[0];

  const { data: lastMonthMetrics } = await supabase
    .from('usage_metrics')
    .select('active_users')
    .gte('date', lastMonthStartStr)
    .lte('date', lastMonthEndStr);

  const lastMonthAvgUsers = lastMonthMetrics && lastMonthMetrics.length > 0
    ? lastMonthMetrics.reduce((s, m) => s + (m.active_users || 0), 0) / lastMonthMetrics.length
    : 0;

  const growthRate = lastMonthAvgUsers > 0
    ? Math.round(((totalUsers - lastMonthAvgUsers) / lastMonthAvgUsers) * 100 * 10) / 10
    : 0;

  return {
    total_tenants: all.length,
    active_tenants: active.length,
    trial_tenants: trial.length,
    total_end_users: totalUsers,
    mrr, arr: mrr * 12,
    trips_this_month: tripsThisMonth,
    ai_requests_this_month: aiRequestsThisMonth,
    growth_rate: growthRate,
    churn_rate: 0,
  };
}

// ── Tenant Config (public API for the App) ───────────────────────────────────

export async function fetchTenantConfig(domain: string): Promise<TenantConfigResponse | null> {
  let { data } = await supabase.from('tenants')
    .select(`*, plan:tenant_plans(*), branding:branding_configs(*), features:feature_configs(*)`)
    .eq('subdomain', domain).maybeSingle();

  if (!data) {
    const r = await supabase.from('tenants')
      .select(`*, plan:tenant_plans(*), branding:branding_configs(*), features:feature_configs(*)`)
      .eq('custom_domain', domain).maybeSingle();
    data = r.data;
  }
  if (!data) return null;

  return {
    tenant_id: data.id,
    status: data.status,
    plan: (data.plan as TenantPlan)?.name as PlanName,
    branding: data.branding as BrandingConfig,
    features: data.features as FeatureConfig,
    max_users: (data.plan as TenantPlan)?.max_users || null,
    max_trips_per_month: (data.plan as TenantPlan)?.max_trips_per_month || null,
  };
}

// ── Audit Log ────────────────────────────────────────────────────────────────

async function logAudit(
  tenantId: string | null, action: string, resourceType: string,
  resourceId: string | null, metadata: Record<string, unknown>
) {
  try {
    await supabase.from('audit_log').insert({
      tenant_id: tenantId,
      actor_id: '00000000-0000-0000-0000-000000000000',
      action, resource_type: resourceType, resource_id: resourceId, metadata,
    });
  } catch { /* silent */ }
}

export async function fetchAuditLog(tenantId?: string, limit = 50): Promise<AuditLog[]> {
  let q = supabase.from('audit_log').select('*').order('created_at', { ascending: false }).limit(limit);
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/** Fetch audit log entries within a date range (inclusive) */
export async function fetchAuditLogByDateRange(from: string, to: string): Promise<AuditLog[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('*')
    .gte('created_at', from)
    .lte('created_at', to + 'T23:59:59.999Z')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data || [];
}

/** Delete audit log entries by their IDs. Returns the count of deleted rows. */
export async function deleteAuditLogsByIds(ids: string[]): Promise<number> {
  if (ids.length === 0) return 0;
  const { error, count } = await supabase
    .from('audit_log')
    .delete({ count: 'exact' })
    .in('id', ids);
  if (error) throw error;
  return count ?? ids.length;
}

// ── App DB Provisioning (cross-database via edge functions) ─────────────────

const APP_SUPABASE_URL = import.meta.env.VITE_APP_SUPABASE_URL || '';
const PROVISION_SECRET = import.meta.env.VITE_PROVISION_SECRET || '';
const PLATFORM_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';

/**
 * Provision B2C pricing plans + features in the App DB for a new tenant.
 * Called after createTenant() succeeds in the Platform DB.
 * Idempotent — safe to call multiple times.
 */
export async function provisionTenantInAppDb(
  tenantId: string,
  tenantName: string,
): Promise<{ ok: boolean; message?: string }> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] App DB provisioning skipped — missing VITE_APP_SUPABASE_URL or VITE_PROVISION_SECRET');
    return { ok: false, message: 'Missing env vars' };
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/provision-tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({ tenant_id: tenantId, tenant_name: tenantName }),
  });

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] provision-tenant failed:', json);
    throw new Error(json.error || 'Provisioning failed');
  }

  console.log('[api] provision-tenant success:', json);
  return json;
}

/**
 * Provision subdomain: creates Cloudflare CNAME + Netlify domain alias.
 * Called after createTenant() — fire-and-forget, non-blocking.
 */
async function provisionSubdomain(slug: string): Promise<void> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) {
    console.warn('[api] provisionSubdomain skipped — no auth session');
    return;
  }

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/provision-subdomain`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ slug }),
  });

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] provision-subdomain failed:', json);
    return;
  }

  console.log('[api] provision-subdomain success:', json);
}

/**
 * Cleanup subdomain: deletes Cloudflare CNAME + Netlify domain alias + custom hostnames.
 * Called before tenant deletion — non-fatal, errors logged but don't block deletion.
 */
async function cleanupSubdomain(
  slug: string,
  customDomains?: { domain: string; cloudflare_hostname_id: string | null }[],
): Promise<void> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) {
    console.warn('[api] cleanupSubdomain skipped — no auth session');
    return;
  }

  try {
    const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/cleanup-subdomain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ slug, custom_domains: customDomains }),
    });

    const json = await res.json();
    if (!res.ok) {
      console.error('[api] cleanup-subdomain failed:', json);
      return;
    }

    console.log('[api] cleanup-subdomain success:', json);
  } catch (err) {
    console.error('[api] cleanup-subdomain exception (non-fatal):', err);
  }
}

/**
 * Fetch users belonging to a tenant from the App DB.
 * Used by Platform admin to see end-users for a given tenant.
 */
export interface AppTenantUser {
  user_id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  role: string;
  status: string;
  joined_at: string;
  plan_key: string;
  plan_name: string;
  subscription_status: string;
  /** True when the row is a Supabase anonymous user (signInAnonymously)
   *  who hasn't yet claimed their account by attaching email +
   *  password. Admin UIs render these with a "Guest" badge and a
   *  "Hide guests" filter so they don't get confused with onboarded
   *  members. The user has full tenant_users membership otherwise. */
  is_anonymous?: boolean;
  /** Most recent activity (max of sign-in / trip create / chat / feature use). NULL when the user has never been active. */
  last_active_at?:  string | null;
  last_sign_in_at?: string | null;
  last_trip_at?:    string | null;
  last_chat_at?:    string | null;
  /** Per-user grants set by Platform admins. Open-shape map of
   *  right_key -> boolean. Known keys today:
   *    • itinerary_template — unlocks the Template tab on App ResultsScreen.
   *  Empty object when no grants are issued. */
  user_rights?: Record<string, boolean>;
  /** Creator Program tier. 'starter' is the default (column NOT NULL
   *  DEFAULT 'starter' on App profiles). 'featured' is set either by
   *  the pg_cron auto-promote job at 50 sales / 500 followers OR
   *  manually via the Promote button (set-creator-tier edge fn). */
  creator_tier?: 'featured' | 'starter';
  /** ISO timestamp of the last promotion to 'featured'. NULL while the
   *  user is a Starter (cleared on demote). Useful in the UI to show
   *  "Featured since {date}". Column name matches the App profiles
   *  schema (creator_tier_promoted_at) — the long name was set by the
   *  original creator_program migration. */
  creator_tier_promoted_at?: string | null;
  /** Confirmed Blueprint sales count — the organic-promote signal #1.
   *  Updated by the payment-succeeded trigger. UI surfaces N / 50 as a
   *  progress hint next to the Promote button. */
  confirmed_blueprint_sales?: number;
  /** Follower count — the organic-promote signal #2. Maintained by the
   *  follow/unfollow trigger. UI surfaces N / 500. */
  follower_count?: number;
}

/** Right keys recognized by the App. Add new keys here as they ship so
 *  the Platform UI can render a matching toggle. The wire format is
 *  open — set-user-right will write any snake_case key — but only keys
 *  on this list actually do anything on the App side. */
export type UserRightKey = 'itinerary_template';

export interface AppTenantTrip {
  id: string;
  destinations: string[];
  destination_label: string;
  status: string;
  owner_name: string;
  owner_email: string;
  travelers: number;
  created_at: string;
  activated_at: string | null;
  archived_at: string | null;
  /**
   * How the trip was created. NULL = AI-generated (Planned trip).
   * Imported trips carry one of url/pdf/word/paste — the platform
   * trips table shows this as a "Type" chip and the Top Destinations
   * rollup breaks down counts by planned vs imported.
   */
  source_type: 'url' | 'pdf' | 'word' | 'paste' | null;
  /** Original URL for source_type='url' imports. NULL otherwise. */
  source_url: string | null;
}

export async function fetchTenantTrips(tenantId: string): Promise<AppTenantTrip[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchTenantTrips skipped — missing env vars');
    return [];
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/tenant-trips?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET',
      headers: { 'x-provision-secret': PROVISION_SECRET },
    },
  );

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] tenant-trips failed:', json);
    return [];
  }

  return json.trips ?? [];
}

// ── Tenant Stories (public-feed journals) ────────────────────────────
// Feeds the new tenant-admin Stories page. Operator-side view of every
// journal belonging to a tenant — drafts AND published AND hidden.
// Enriched server-side with author + reel + trip destinations so each
// row is self-contained and the Platform doesn't need follow-up
// queries per row. Server: apps/unravel-app-v2/supabase/functions/
// tenant-list-journals/index.ts.
//
// Visibility states the UI derives client-side from these flags:
//   • Draft       → published_at IS NULL
//   • Published   → published_at IS NOT NULL AND tenant_hidden_at IS NULL
//   • Hidden      → published_at IS NOT NULL AND tenant_hidden_at IS NOT NULL
// "Hidden" implies the row WAS public at some point — a draft can't be
// hidden because there's nothing to hide.
export interface AppTenantJournal {
  id:                    string;
  title:                 string | null;
  body_markdown:         string | null;
  created_at:            string;
  /** ISO timestamp of the row's last UPDATE, set by the universal
   *  set_updated_at trigger on journal_entries. NOTE: also bumped by
   *  the Platform's own hide/unhide actions — UI should compare against
   *  published_at and only surface "edited" when the gap is meaningful
   *  so moderation drift doesn't look like an author edit. */
  updated_at:            string | null;
  /** ISO timestamp when the AUTHOR published. NULL = still a draft. */
  published_at:          string | null;
  /** ISO timestamp when the OPERATOR hid this from the public feed.
   *  NULL = not hidden. See migration 20260728_journal_tenant_moderation. */
  tenant_hidden_at:      string | null;
  /** Canonical moderation category from Phase 3C. One of
   *  MODERATION_REASON_CODES; null on rows hidden before 3C migrated
   *  (the freeform text lives in tenant_hidden_notes for those). */
  tenant_hidden_reason_code: string | null;
  /** Optional operator notes attached to a hide action. Was formerly
   *  `tenant_hidden_reason` before Phase 3C split it into code + notes;
   *  the freeform value is preserved here verbatim. */
  tenant_hidden_notes:   string | null;
  day_number:            number | null;
  cover_photo_url:       string | null;
  photos:                string[];
  feed_likes_count:      number;
  feed_comments_count:   number;
  trip_id:               string;
  user_id:               string;
  author: {
    user_id:     string;
    display_name: string;
    /** Author's email — Platform-side only, used by the Stories page
     *  so the operator can contact them directly about moderation. */
    email:       string | null;
    avatar_url:  string | null;
  };
  reel: {
    mp4_url:       string | null;
    thumbnail_url: string | null;
  } | null;
  destinations: string[];
}

export async function fetchTenantJournals(tenantId: string): Promise<AppTenantJournal[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchTenantJournals skipped — missing env vars');
    return [];
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/tenant-list-journals?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET',
      headers: { 'x-provision-secret': PROVISION_SECRET },
    },
  );

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] tenant-list-journals failed:', json);
    return [];
  }
  return json.journals ?? [];
}

/** Orchestrated tenant-journal moderation. Replaces the direct
 *  Platform→App call with a single round-trip to the
 *  `moderate-tenant-journal` Platform edge fn, which:
 *
 *    1. JWT-validates the caller and checks platform_admin OR
 *       tenant_admin-for-this-tenant membership.
 *    2. Proxies the hide/unhide to the App's
 *       tenant-toggle-journal-visibility (still authoritative).
 *    3. Writes a row to platform_audit_log so we have a permanent
 *       record of who hid what.
 *    4. (Optional) Emails the author via Resend when hiding, so they
 *       aren't surprised that their story disappeared.
 *
 *  Returns `{ ok, emailed }`. `emailed` only flips true when:
 *    - hide=true
 *    - authorEmail was passed
 *    - RESEND_API_KEY is set on the edge fn
 *    - Resend accepted the request
 *  Otherwise it's false but the moderation still succeeded — callers
 *  should treat `emailed` as best-effort, not a contract.
 *
 *  The optional `authorEmail` / `storyTitle` are nice-to-haves the UI
 *  has on hand from the Stories listing; the edge fn doesn't query
 *  them itself to keep the round-trip count down. */
export async function moderateTenantJournal(params: {
  tenantId:     string;
  journalId:    string;
  hide:         boolean;
  /** Phase 3C — canonical category code, one of MODERATION_REASON_CODES.
   *  Only meaningful when hide=true. */
  reasonCode?:  string;
  /** Phase 3C — human label for the reason code. The orchestrator uses
   *  it verbatim in the author email so we don't have to keep a
   *  duplicate label table server-side. Resolved client-side from
   *  `reasonMeta()`. */
  reasonLabel?: string;
  /** Free-text notes attached to the hide. ≤500 chars. */
  notes?:       string;
  /** @deprecated — legacy alias for `notes`. Kept while in-flight
   *  callers migrate. */
  reason?:      string;
  authorEmail?: string;
  storyTitle?:  string;
}): Promise<{ ok: boolean; emailed: boolean; error?: string }> {
  if (!PLATFORM_SUPABASE_URL) {
    console.warn('[api] moderateTenantJournal skipped — missing Platform URL');
    return { ok: false, emailed: false, error: 'Platform Supabase not configured' };
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { ok: false, emailed: false, error: 'Not authenticated' };
  }

  // Pre-3C callers pass `reason` — collapse it onto `notes` so the
  // server sees the new field name. Server still accepts both, but we
  // canonicalise here so the new shape is what flows over the wire.
  const notes = params.notes ?? params.reason;

  const res = await fetch(
    `${PLATFORM_SUPABASE_URL}/functions/v1/moderate-tenant-journal`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        tenant_id:    params.tenantId,
        journal_id:   params.journalId,
        hide:         params.hide,
        // Only include reason fields when hiding — the orchestrator +
        // App fn both drop them on unhide anyway.
        ...(params.hide && params.reasonCode  ? { reason_code:  params.reasonCode  } : {}),
        ...(params.hide && params.reasonLabel ? { reason_label: params.reasonLabel } : {}),
        ...(params.hide && notes              ? { notes }                              : {}),
        // Best-effort hints for the audit row + email step. The edge
        // fn doesn't depend on these being present.
        ...(params.authorEmail ? { author_email: params.authorEmail } : {}),
        ...(params.storyTitle  ? { story_title:  params.storyTitle  } : {}),
      }),
    },
  );

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    console.error('[api] moderate-tenant-journal failed:', json);
    return { ok: false, emailed: false, error: json?.error || `HTTP ${res.status}` };
  }
  return { ok: true, emailed: Boolean(json?.emailed) };
}

// ── Phase 3A — Story reports ───────────────────────────────────────

/** Summary row from tenant-list-reports — one entry per journal that
 *  has ≥1 OPEN reader report. The Stories page joins this against its
 *  existing journals list via journal_id. */
export interface TenantReportSummary {
  journal_id: string;
  open_count: number;
  latest_at:  string;
  reasons:    Record<string, number>;
}

/** Full per-report row used by the Reports modal. */
export interface TenantReportDetail {
  id:               string;
  reason_code:      string;
  notes:            string | null;
  created_at:       string;
  dismissed_at:     string | null;
  dismissed_reason: string | null;
  reporter: {
    user_id:      string;
    display_name: string;
    email:        string | null;
    avatar_url:   string | null;
  };
}

/** Fetch per-tenant report aggregates. Used to render the per-row
 *  Reported badge + "Needs review" auto-flag on the Stories table. */
export async function fetchTenantReports(tenantId: string): Promise<TenantReportSummary[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchTenantReports skipped — missing env');
    return [];
  }
  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/tenant-list-reports?tenant_id=${encodeURIComponent(tenantId)}`,
    { method: 'GET', headers: { 'x-provision-secret': PROVISION_SECRET } },
  );
  if (!res.ok) {
    console.error('[api] tenant-list-reports failed:', await res.text());
    return [];
  }
  const json = await res.json();
  return (json.summary ?? []) as TenantReportSummary[];
}

/** Fetch full report list for one journal — both open and dismissed,
 *  so the modal can surface the operator's prior decisions. */
export async function fetchJournalReports(
  tenantId:  string,
  journalId: string,
): Promise<TenantReportDetail[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) return [];
  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/tenant-list-reports?tenant_id=${encodeURIComponent(tenantId)}&journal_id=${encodeURIComponent(journalId)}`,
    { method: 'GET', headers: { 'x-provision-secret': PROVISION_SECRET } },
  );
  if (!res.ok) return [];
  const json = await res.json();
  return (json.reports ?? []) as TenantReportDetail[];
}

/** Dismiss every open report on a journal. Routes through the same
 *  Platform orchestrator that handles hide/unhide so the operator's
 *  identity is JWT-validated and the audit log gets a
 *  `journal_reports_dismissed` row. Optional reason is stored on each
 *  dismissed row for trail. */
export async function dismissJournalReports(params: {
  tenantId:  string;
  journalId: string;
  reason?:   string;
}): Promise<{ ok: boolean; dismissed: number; error?: string }> {
  if (!PLATFORM_SUPABASE_URL) {
    return { ok: false, dismissed: 0, error: 'Platform Supabase not configured' };
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { ok: false, dismissed: 0, error: 'Not authenticated' };
  }
  const res = await fetch(
    `${PLATFORM_SUPABASE_URL}/functions/v1/moderate-tenant-journal`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        // New action dispatcher. The orchestrator branches on `action`
        // when present; absent = legacy hide/unhide path.
        action:    'dismiss_reports',
        tenant_id: params.tenantId,
        journal_id: params.journalId,
        ...(params.reason ? { dismissed_reason: params.reason } : {}),
      }),
    },
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, dismissed: 0, error: json?.error || `HTTP ${res.status}` };
  }
  return { ok: true, dismissed: Number(json?.dismissed ?? 0) };
}

// ── Unified moderation queue — reports across ALL surfaces ─────────
// journals + blueprints (story_reports) + moments (wall_reports) +
// journeys (reel_reports). Lets the operator see + clear every flagged
// piece of content, not just journals.

export type ReportTargetType =
  | 'journal'
  | 'poster'
  | 'wall_video'
  | 'wall_cleanup'
  | 'wall_upload'
  | 'reel';

/** One flagged piece of content with its open-report aggregate. */
export interface TenantFlaggedItem {
  target_type: ReportTargetType;
  target_id:   string;
  open_count:  number;
  latest_at:   string;
  reasons:     Record<string, number>;
  title:       string | null;
}

/** Fetch the unified flagged-content queue for a tenant — every surface,
 *  one entry per reported item. Backs the "Flagged content" panel. */
export async function fetchAllTenantReports(tenantId: string): Promise<TenantFlaggedItem[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchAllTenantReports skipped — missing env');
    return [];
  }
  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/tenant-list-reports?tenant_id=${encodeURIComponent(tenantId)}&scope=all`,
    { method: 'GET', headers: { 'x-provision-secret': PROVISION_SECRET } },
  );
  if (!res.ok) {
    console.error('[api] tenant-list-reports (scope=all) failed:', await res.text());
    return [];
  }
  const json = await res.json();
  return (json.flagged ?? []) as TenantFlaggedItem[];
}

/** Dismiss every open report on one piece of content of any type. Routes
 *  through the same orchestrator as journal dismissals so the operator's
 *  identity is JWT-validated and the audit log gets a row. */
export async function dismissContentReports(params: {
  tenantId:   string;
  targetType: ReportTargetType;
  targetId:   string;
  reason?:    string;
}): Promise<{ ok: boolean; dismissed: number; error?: string }> {
  if (!PLATFORM_SUPABASE_URL) {
    return { ok: false, dismissed: 0, error: 'Platform Supabase not configured' };
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { ok: false, dismissed: 0, error: 'Not authenticated' };
  }
  const res = await fetch(
    `${PLATFORM_SUPABASE_URL}/functions/v1/moderate-tenant-journal`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action:      'dismiss_reports',
        tenant_id:   params.tenantId,
        target_type: params.targetType,
        target_id:   params.targetId,
        ...(params.reason ? { dismissed_reason: params.reason } : {}),
      }),
    },
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, dismissed: 0, error: json?.error || `HTTP ${res.status}` };
  }
  return { ok: true, dismissed: Number(json?.dismissed ?? 0) };
}

/** @deprecated — kept as a thin shim while in-flight callers migrate to
 *  `moderateTenantJournal`. New callers should use the orchestrator
 *  directly so audit-log + author-email side effects happen. */
export async function setTenantJournalHidden(
  tenantId:  string,
  journalId: string,
  hide:      boolean,
  reason?:   string,
): Promise<boolean> {
  const r = await moderateTenantJournal({ tenantId, journalId, hide, reason });
  return r.ok;
}

/** A single row of plan-stay history for a B2C user. Mirrors the
 *  user_subscription_history table on the App DB (snapshotted plan name
 *  + price at the time of the change so history is honest even after
 *  the tenant later renames or reprices their plans). */
export interface AppUserSubscriptionHistory {
  id:                  string;
  plan_id:             string | null;
  plan_key:            string | null;
  plan_name:           string | null;
  monthly_price:       number | null;
  annual_price:        number | null;
  currency:            string | null;
  is_free:             boolean;
  status:              string;
  billing_period:      string | null;
  /** signup | upgrade | downgrade | cancel | reactivate | renewal | admin_change */
  change_reason:       string;
  started_at:          string;
  ended_at:            string | null;
}

/** Reset a tenant's Starter-plan eligibility (Platform DB). Calls the
 *  `reset_tenant_starter_eligibility` RPC, which is gated server-side
 *  by `is_platform_admin()` — non-super-admins get a clear permission
 *  error from the RPC even if they manage to invoke this. */
export async function resetTenantStarterEligibility(
  tenantId: string,
): Promise<{ ok: boolean; previously_used_at?: string; error?: string }> {
  const { data, error } = await supabase.rpc('reset_tenant_starter_eligibility', {
    p_tenant_id: tenantId,
  });
  if (error) {
    console.error('[api] reset_tenant_starter_eligibility failed:', error);
    return { ok: false, error: error.message };
  }
  return { ok: true, previously_used_at: (data as any)?.previously_used_at };
}

/** Reset an end-user's free-plan eligibility (App DB, cross-DB). Hits
 *  the App-DB `reset-user-free-plan` edge function gated by
 *  PROVISION_SECRET. Should only be invoked by super admins; the
 *  Platform UI only renders the trigger button under that condition. */
export async function resetUserFreePlanEligibility(
  tenantId: string,
  userId:   string,
): Promise<{ ok: boolean; error?: string }> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    return { ok: false, error: 'Missing App DB env vars' };
  }
  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/reset-user-free-plan`,
    {
      method: 'POST',
      headers: {
        'Content-Type':      'application/json',
        'x-provision-secret': PROVISION_SECRET,
      },
      body: JSON.stringify({ tenant_id: tenantId, user_id: userId }),
    },
  );
  const json = await res.json();
  if (!res.ok) {
    console.error('[api] reset-user-free-plan failed:', json);
    return { ok: false, error: json.error || 'Reset failed' };
  }
  return { ok: true };
}

/** Fetch a single end-user's plan-change history within a tenant. Hits
 *  the App-DB `user-subscription-history` edge function (gated by
 *  PROVISION_SECRET — Platform admins are the only callers). */
export async function fetchUserSubscriptionHistory(
  tenantId: string,
  userId:   string,
): Promise<AppUserSubscriptionHistory[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchUserSubscriptionHistory skipped — missing env vars');
    return [];
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/user-subscription-history?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`,
    {
      method: 'GET',
      headers: { 'x-provision-secret': PROVISION_SECRET },
    },
  );

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] user-subscription-history failed:', json);
    return [];
  }
  return json.history ?? [];
}

export async function fetchTenantUsers(tenantId: string): Promise<AppTenantUser[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchTenantUsers skipped — missing env vars');
    return [];
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/tenant-users?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET',
      headers: { 'x-provision-secret': PROVISION_SECRET },
    },
  );

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] tenant-users failed:', json);
    return [];
  }

  return json.users ?? [];
}

// ── PWA Install Stats (App DB) ───────────────────────────────────────────────

export interface PwaInstallStats {
  total: number;                // real (non-guest) users
  installed: number;            // have the PWA installed somewhere
  activeStandalone30d: number;  // opened the installed app in last 30d
  nudgedAny: number;            // got ≥1 WhatsApp install nudge
  nudgedCapped: number;         // hit the 2-nudge cap, still not installed
  whatsappTotal: number;        // WhatsApp cohort size
  whatsappInstalled: number;    // WhatsApp users who installed
  tenants?: PwaInstallTenantRow[]; // present when breakdown requested
}

export interface PwaInstallTenantRow {
  tenant_id: string;
  total: number;
  installed: number;
  activeStandalone30d: number;
  nudgedAny: number;
  nudgedCapped: number;
  whatsappTotal: number;
  whatsappInstalled: number;
}

/** How many app users have installed the PWA, and how the install nudge is
 *  doing. Reads the pwa_* columns on the App DB's profiles via the
 *  pwa-install-stats edge function. Pass { breakdown: true } for a per-tenant
 *  breakdown (`tenants`), or { tenantId } to scope the totals to a single
 *  tenant (for the tenant-manage console). Returns null when unreachable so the
 *  caller can render a quiet "unavailable" state rather than crash. */
export async function fetchPwaInstallStats(opts?: { breakdown?: boolean; tenantId?: string }): Promise<PwaInstallStats | null> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchPwaInstallStats skipped — missing App DB env vars');
    return null;
  }
  try {
    const params = new URLSearchParams();
    if (opts?.tenantId) params.set('tenant_id', opts.tenantId);
    else if (opts?.breakdown) params.set('breakdown', '1');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/pwa-install-stats${qs}`, {
      method: 'GET',
      headers: { 'x-provision-secret': PROVISION_SECRET },
    });
    const json = await res.json();
    if (!res.ok || !json?.ok) {
      console.error('[api] pwa-install-stats failed:', json);
      return null;
    }
    return {
      total: json.total ?? 0,
      installed: json.installed ?? 0,
      activeStandalone30d: json.activeStandalone30d ?? 0,
      nudgedAny: json.nudgedAny ?? 0,
      nudgedCapped: json.nudgedCapped ?? 0,
      whatsappTotal: json.whatsappTotal ?? 0,
      whatsappInstalled: json.whatsappInstalled ?? 0,
      ...(Array.isArray(json.tenants) ? { tenants: json.tenants as PwaInstallTenantRow[] } : {}),
    };
  } catch (e) {
    console.error('[api] pwa-install-stats error:', e);
    return null;
  }
}

// ── Manage Tenant Users (App DB) ─────────────────────────────────────────────

/** Correct a user's email after a registration typo. Calls the App DB
 *  update-user-email edge function which uses the Auth admin API
 *  (bypasses the verification challenge — appropriate because an admin
 *  is making the change, not the user). Also syncs the denormalized
 *  profiles.email row. Refuses to operate on anonymous (guest) users.
 *
 *  Throws with a friendly message on validation/conflict errors so the
 *  modal can surface them inline. */
export async function updateUserEmail(args: {
  userId:        string;
  newEmail:      string;
  actorEmail?:   string;
}): Promise<{ id: string; email: string; warning?: string }> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/update-user-email`, {
    method: 'POST',
    headers: {
      'Content-Type':       'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({
      user_id:     args.userId,
      new_email:   args.newEmail,
      actor_email: args.actorEmail,
    }),
  });

  let json: any = null;
  try { json = await res.json(); } catch { /* keep null */ }

  if (!res.ok) {
    throw new Error(json?.error || `update-user-email HTTP ${res.status}`);
  }
  return {
    id:      json.user.id,
    email:   json.user.email,
    warning: json.warning,
  };
}

/** Toggle a single per-user grant on App DB tenant_users.user_rights.
 *  Calls the App's set-user-right edge fn with PROVISION_SECRET. The
 *  edge fn merges {rightKey: value} into the existing jsonb so other
 *  grants on the same user are preserved.
 *
 *  Use `UserRightKey` for `rightKey` to stay in step with the App's
 *  known keys. Returns the new full user_rights object so the caller
 *  can update local state without a re-fetch. */
export async function setUserRight(args: {
  tenantId:    string;
  userId:      string;
  rightKey:    UserRightKey | string;
  value:       boolean;
  actorId?:    string;
  actorEmail?: string;
  reason?:     string;
}): Promise<{ user_rights: Record<string, boolean> }> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/set-user-right`, {
    method: 'POST',
    headers: {
      'Content-Type':       'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({
      tenant_id:   args.tenantId,
      user_id:     args.userId,
      right_key:   args.rightKey,
      value:       args.value,
      actor_id:    args.actorId,
      actor_email: args.actorEmail,
      reason:      args.reason,
    }),
  });

  let json: any = null;
  try { json = await res.json(); } catch { /* keep null */ }
  if (!res.ok) {
    throw new Error(json?.error || `set-user-right HTTP ${res.status}`);
  }
  return { user_rights: (json?.user_rights ?? {}) as Record<string, boolean> };
}

/** Manually flip a creator's tier on the App project's profiles table.
 *  Calls the App's set-creator-tier edge fn with PROVISION_SECRET.
 *
 *  The organic path is the pg_cron auto-promote job (50 sales OR 500
 *  followers); this helper is the Platform-admin override for:
 *    • Pre-promoting a known influencer before they hit the threshold.
 *    • Demoting someone gaming the counters or violating policy.
 *
 *  Idempotent on the server — setting the same tier twice is a no-op
 *  (no DB write, no audit row). The response includes `unchanged: true`
 *  on the idempotent path so the UI can skip the success toast.
 *
 *  Returns the new tier so the caller can splice it into local state
 *  without a re-fetch. */
export async function setCreatorTier(args: {
  tenantId:    string;
  userId:      string;
  tier:        'featured' | 'starter';
  actorId?:    string;
  actorEmail?: string;
  reason?:     string;
}): Promise<{ creator_tier: 'featured' | 'starter'; creator_tier_promoted_at: string | null; unchanged?: boolean }> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/set-creator-tier`, {
    method: 'POST',
    headers: {
      'Content-Type':       'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({
      tenant_id:   args.tenantId,
      user_id:     args.userId,
      tier:        args.tier,
      actor_id:    args.actorId,
      actor_email: args.actorEmail,
      reason:      args.reason,
    }),
  });

  let json: any = null;
  try { json = await res.json(); } catch { /* keep null */ }
  if (!res.ok) {
    throw new Error(json?.error || `set-creator-tier HTTP ${res.status}`);
  }
  return {
    creator_tier:             (json?.creator_tier ?? args.tier) as 'featured' | 'starter',
    creator_tier_promoted_at: (json?.creator_tier_promoted_at ?? null) as string | null,
    unchanged:                !!json?.unchanged,
  };
}

export async function changeUserRole(
  tenantId: string,
  userId: string,
  role: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-tenant-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({ tenant_id: tenantId, user_id: userId, action: 'change_role', role }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to change role');
}

export interface RemoveUserResult {
  /** True when this was the user's last tenant membership and the
   *  auth.users row was deleted along with it (cascading to profiles
   *  and other per-user data). False when the user is still a member
   *  of one or more other tenants and only this membership was
   *  removed — the auth user lives on. */
  authUserDeleted:      boolean;
  /** How many tenant memberships the user has AFTER this remove.
   *  0 means the user account is gone. >0 means they're still
   *  active elsewhere. */
  remainingMemberships: number;
}

export async function removeUser(
  tenantId: string,
  userId: string,
): Promise<RemoveUserResult> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-tenant-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({ tenant_id: tenantId, user_id: userId, action: 'remove' }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to remove user');
  return {
    authUserDeleted:      Boolean(json.auth_user_deleted),
    remainingMemberships: Number(json.remaining_memberships ?? 0),
  };
}

export async function blockUser(
  tenantId: string,
  userId: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-tenant-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({ tenant_id: tenantId, user_id: userId, action: 'block' }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to block user');
}

export async function unblockUser(
  tenantId: string,
  userId: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-tenant-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({ tenant_id: tenantId, user_id: userId, action: 'unblock' }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to unblock user');
}

export async function syncTenantAdminRole(
  tenantId: string,
  email: string,
  action: 'promote' | 'demote',
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/sync-tenant-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ tenant_id: tenantId, email, action }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to sync admin role');
}

// ── Tenant Deletion (Support Tools) ──────────────────────────────────────────

/**
 * Delete all App DB data for a tenant.
 * Calls the delete-tenant edge function which runs cleanup_entire_tenant RPC.
 */
export async function deleteTenantAppData(tenantId: string): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/delete-tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({ tenant_id: tenantId }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to delete tenant app data');
  console.log('[api] delete-tenant success:', json);
}

/**
 * Fully delete a tenant:
 *   1. Clean up Cloudflare DNS + Netlify domain aliases (non-fatal)
 *   2. Delete all App DB data (cleanup_entire_tenant RPC):
 *        - per-user: trips (cascades flights/itineraries/budgets/etc),
 *          collaborators, invitations, subscriptions, notifications,
 *          push, feature overrides + usage, folders, presence,
 *          referrals, receipts, scanned_documents (PII), foodie_stamps,
 *          ai_requests
 *        - tenant-level: tenant_users, plan/pricing config, audit log,
 *          live_sessions, feedback, ai_requests (anonymous traffic),
 *          data_retention_policies (overrides), tenant_stripe_settings
 *        - storage: passport/visa/hotel scan files in `documents` bucket
 *          and foodie photos in `foodie-photos` bucket are removed
 *          immediately by the delete-tenant edge function. boarding-pass
 *          photos cascade out via trips and are swept by the daily
 *          orphan-sweep cron within 24h.
 *   3. Delete from Platform DB (CASCADE handles all related tables)
 *   4. Audit log the action
 */
export async function deleteTenantFull(tenantId: string): Promise<void> {
  // Step 1: Fetch tenant data BEFORE deletion (need subdomain + custom domains)
  const { data: tenant } = await supabase
    .from('tenants')
    .select('subdomain')
    .eq('id', tenantId)
    .single();

  const { data: customDomains } = await supabase
    .from('tenant_domains')
    .select('domain, cloudflare_hostname_id')
    .eq('tenant_id', tenantId);

  // Step 2: Clean up Cloudflare CNAME + Netlify alias + custom hostnames (non-fatal)
  if (tenant?.subdomain) {
    const slug = tenant.subdomain.replace('.discoverygem.com', '');
    await cleanupSubdomain(slug, customDomains ?? undefined);
  }

  // Step 3: Clean up App DB
  await deleteTenantAppData(tenantId);

  // Step 4: Delete from Platform DB (CASCADE handles:
  //   tenant_admins, branding_configs, feature_configs, tenant_domains,
  //   usage_metrics, billing_events, api_keys, announcements,
  //   tenant_bulletins, booking_inquiries, contact_inquiries,
  //   tenant_referral_codes, tenant_referrals, referral_commissions,
  //   referral_payouts, tenant_affiliate_partners, affiliate_click_events,
  //   tenant_referral_config, audit_log)
  const { error } = await supabase
    .from('tenants')
    .delete()
    .eq('id', tenantId);

  if (error) throw error;

  // Step 5: Audit (fire-and-forget — tenant row is gone, use null tenant_id)
  logAudit(null, 'tenant_deleted', 'tenant', tenantId, {}).catch(() => {});
}

// ── B2C Pricing (App DB cross-DB calls) ──────────────────────────────────────

export async function fetchTenantPricing(tenantId: string): Promise<AppTenantPricingPlan[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchTenantPricing skipped — missing env vars');
    return [];
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/fetch-tenant-pricing?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET',
      headers: { 'x-provision-secret': PROVISION_SECRET },
    },
  );

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] fetch-tenant-pricing failed:', json);
    return [];
  }

  return json.plans ?? [];
}

export async function updateTenantPricing(
  tenantId: string,
  plans: UpdatePricingPlanInput[],
): Promise<AppTenantPricingPlan[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/update-tenant-pricing`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-provision-secret': PROVISION_SECRET,
      },
      body: JSON.stringify({ tenant_id: tenantId, plans }),
    },
  );

  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.error || 'Failed to update pricing');
  }

  return json.plans ?? [];
}

// ── B2C Plan Builder (Tenant Admin) ───────────────────────────────────────────

export async function fetchB2CPlansForBuilder(tenantId: string): Promise<import('../types').B2CPlanManagerData> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchB2CPlansForBuilder skipped — missing env vars');
    return { plans: [], features: [], planFeatures: [], subscriberCounts: {} };
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans?tenant_id=${encodeURIComponent(tenantId)}`,
    { method: 'GET', headers: { 'x-provision-secret': PROVISION_SECRET } },
  );

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] manage-b2c-plans GET failed:', json);
    return { plans: [], features: [], planFeatures: [], subscriberCounts: {} };
  }

  return json;
}

export async function createB2CPlan(
  tenantId: string,
  plan: import('../types').B2CPlanInput,
  actorEmail?: string,
  limits?: { max_b2c_plans: number; max_b2c_free_plans: number },
  tenantName?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) throw new Error('Missing App DB env vars');

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify({
      action: 'create_plan',
      tenant_id: tenantId,
      tenant_name: tenantName,
      plan,
      actor_email: actorEmail,
      max_b2c_plans: limits?.max_b2c_plans ?? -1,
      max_b2c_free_plans: limits?.max_b2c_free_plans ?? -1,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create B2C plan');
}

export async function updateB2CPlan(
  tenantId: string,
  planId: string,
  updates: Partial<import('../types').B2CPlanInput>,
  actorEmail?: string,
  tenantName?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) throw new Error('Missing App DB env vars');

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify({ action: 'update_plan', tenant_id: tenantId, tenant_name: tenantName, plan_id: planId, updates, actor_email: actorEmail }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update B2C plan');
}

export async function toggleB2CPlan(
  tenantId: string,
  planId: string,
  isActive: boolean,
  actorEmail?: string,
  tenantName?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) throw new Error('Missing App DB env vars');

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify({ action: 'toggle_plan', tenant_id: tenantId, tenant_name: tenantName, plan_id: planId, is_active: isActive, actor_email: actorEmail }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to toggle B2C plan');
}

export async function deleteB2CPlan(
  tenantId: string,
  planId: string,
  actorEmail?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) throw new Error('Missing App DB env vars');

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify({ action: 'delete_plan', tenant_id: tenantId, plan_id: planId, actor_email: actorEmail }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to delete B2C plan');
}

export async function syncB2CPlanStripe(
  tenantId: string,
  planId: string,
  tenantName?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) throw new Error('Missing App DB env vars');

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify({ action: 'sync_stripe', tenant_id: tenantId, plan_id: planId, tenant_name: tenantName }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to sync B2C plan to Stripe');
}

export async function setB2CStripeEnabled(
  tenantId: string,
  enabled: boolean,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) throw new Error('Missing App DB env vars');

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify({ action: 'set_stripe_enabled', tenant_id: tenantId, enabled }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to toggle Stripe payments');
}

export async function syncB2BCeilingToB2C(
  tenantId: string,
  allowedB2cFeatureKeys: string[],
  actorEmail?: string,
): Promise<{ disabled_count: number }> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) throw new Error('Missing App DB env vars');

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-b2c-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
    body: JSON.stringify({ action: 'sync_ceiling', tenant_id: tenantId, allowed_feature_keys: allowedB2cFeatureKeys, actor_email: actorEmail }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to sync B2B ceiling');
  return { disabled_count: json.disabled_count ?? 0 };
}

// ── B2B Checkout (self-service plan upgrade) ─────────────────────────────────

/**
 * Create a Stripe Checkout Session for a B2B tenant plan upgrade.
 * Calls the Platform's b2b-checkout edge function.
 * Returns the Stripe Checkout URL for redirect.
 */
export async function createB2BCheckout(input: {
  tenantId: string;
  planName: string;
  billingCycle: 'monthly' | 'annual';
  seats: number;
  adminEmail: string;
}): Promise<{ url: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const res = await fetch(
    `${PLATFORM_SUPABASE_URL}/functions/v1/b2b-checkout`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        tenant_id: input.tenantId,
        plan_name: input.planName,
        billing_cycle: input.billingCycle,
        seats: input.seats,
        admin_email: input.adminEmail,
      }),
    },
  );

  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.error || 'Failed to create checkout session');
  }

  return { url: json.url };
}

// ── B2C Feature Matrix (Two-Tier Admin) ──────────────────────────────────────

export async function fetchB2CFeatureMatrix(tenantId: string): Promise<import('../types').B2CFeatureMatrix> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchB2CFeatureMatrix skipped — missing env vars');
    return { features: [], plans: [], planFeatures: [] };
  }

  const res = await fetch(
    `${APP_SUPABASE_URL}/functions/v1/manage-plan-features?tenant_id=${encodeURIComponent(tenantId)}`,
    { method: 'GET', headers: { 'x-provision-secret': PROVISION_SECRET } },
  );

  const json = await res.json();
  if (!res.ok) {
    console.error('[api] fetchB2CFeatureMatrix failed:', json);
    return { features: [], plans: [], planFeatures: [] };
  }

  return json;
}

export async function updatePlanFeatures(
  tenantId: string,
  updates: import('../types').PlanFeatureUpdate[],
  actorType: 'super_admin' | 'tenant_admin' = 'super_admin',
  actorId?: string,
  actorEmail?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-plan-features`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      actor_type: actorType,
      actor_id: actorId,
      actor_email: actorEmail,
      updates,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update plan features');
}

export async function fetchUserOverrides(
  tenantId: string,
  userId?: string,
): Promise<import('../types').UserOverride[]> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) return [];

  let url = `${APP_SUPABASE_URL}/functions/v1/manage-user-overrides?tenant_id=${encodeURIComponent(tenantId)}`;
  if (userId) url += `&user_id=${encodeURIComponent(userId)}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: { 'x-provision-secret': PROVISION_SECRET },
  });

  const json = await res.json();
  if (!res.ok) return [];
  return json.overrides ?? [];
}

export async function upsertUserOverride(
  tenantId: string,
  override: import('../types').UserOverrideInput,
  actorId?: string,
  actorEmail?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-user-overrides`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      ...override,
      actor_id: actorId,
      actor_email: actorEmail,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to upsert user override');
}

export async function deleteUserOverride(
  tenantId: string,
  userId: string,
  featureId: string,
  actorId?: string,
  actorEmail?: string,
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }

  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/manage-user-overrides`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      user_id: userId,
      feature_id: featureId,
      actor_id: actorId,
      actor_email: actorEmail,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to delete user override');
}

export async function fetchB2CAuditLog(
  tenantId?: string,
  limit = 50,
  offset = 0,
): Promise<{ entries: import('../types').B2CAuditEntry[]; total: number }> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    return { entries: [], total: 0 };
  }

  let url = `${APP_SUPABASE_URL}/functions/v1/fetch-audit-log?limit=${limit}&offset=${offset}`;
  if (tenantId) url += `&tenant_id=${encodeURIComponent(tenantId)}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: { 'x-provision-secret': PROVISION_SECRET },
  });

  const json = await res.json();
  if (!res.ok) return { entries: [], total: 0 };
  return { entries: json.entries ?? [], total: json.total ?? 0 };
}

// ── Bridge Kill Switch → B2C Ceiling ─────────────────────────────────────────

/**
 * When a Platform kill switch is toggled, propagate to B2C by setting
 * ceiling_enabled on all plans for that feature.
 *   OFF → ceiling_enabled = false, is_enabled = false  (locked + reset)
 *   ON  → ceiling_enabled = null   (removes ceiling, tenant admin re-enables)
 */
export async function bridgeKillSwitchToB2C(
  tenantId: string,
  featureKey: string,
  enabled: boolean,
): Promise<void> {
  try {
    const matrix = await fetchB2CFeatureMatrix(tenantId);
    const feature = matrix.features.find(f => f.key === featureKey);
    if (!feature) {
      console.warn(`[api] bridgeKillSwitch: no B2C feature found for key "${featureKey}"`);
      return;
    }

    const updates: import('../types').PlanFeatureUpdate[] = matrix.plans.map(plan => ({
      plan_id: plan.id,
      feature_id: feature.id,
      ceiling_enabled: enabled ? null : false,
      // When disabling: also reset is_enabled so UI doesn't show confusing
      // "enabled but locked" state. When re-enabling: don't touch is_enabled —
      // tenant admin decides when to re-enable features.
      ...(enabled ? {} : { is_enabled: false }),
    }));

    await updatePlanFeatures(tenantId, updates, 'super_admin');
  } catch (err) {
    console.error('[api] bridgeKillSwitchToB2C failed:', err);
  }
}

// ── Manage Seats ─────────────────────────────────────────────────────────────

/**
 * Update seat count on an existing Stripe subscription.
 * Prorates the change on the next invoice.
 */
export async function updateSeats(input: UpdateSeatsInput): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/update-seats`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      tenant_id: input.tenantId,
      new_quantity: input.newQuantity,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update seats');
}

// ── Payment Method ──────────────────────────────────────────────────────────

export interface PaymentMethodInfo {
  has_payment_method: boolean;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
}

export async function fetchPaymentMethod(tenantId: string): Promise<PaymentMethodInfo> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(
    `${PLATFORM_SUPABASE_URL}/functions/v1/stripe-payment-method?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    },
  );

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to fetch payment method');
  return json;
}

// ── Billing Portal ──────────────────────────────────────────────────────────

export async function createBillingPortalUrl(tenantId: string): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/stripe-billing-portal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ tenant_id: tenantId }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create billing portal');
  return json.url;
}

// ── Invoice History ──────────────────────────────────────────────────────────

/**
 * Fetch real Stripe invoices for a tenant.
 */
export async function fetchInvoices(tenantId: string): Promise<StripeInvoice[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(
    `${PLATFORM_SUPABASE_URL}/functions/v1/fetch-invoices?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    },
  );

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to fetch invoices');
  return json.invoices ?? [];
}

// ── Stripe Connect Onboarding ────────────────────────────────────────────────

/**
 * Create a Stripe Connect account + onboarding link for a tenant.
 * Returns the onboarding URL to redirect the tenant admin.
 */
export async function createConnectOnboardingLink(
  tenantId: string,
  returnUrl: string,
  refreshUrl: string,
): Promise<ConnectOnboardingResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/stripe-connect-onboarding`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      return_url: returnUrl,
      refresh_url: refreshUrl,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create Connect onboarding link');
  return {
    url: json.url,
    account_id: json.account_id,
    auto_onboarded: json.auto_onboarded || false,
  };
}

/**
 * Check the onboarding status of a tenant's Stripe Connect account.
 */
export async function fetchConnectStatus(tenantId: string): Promise<ConnectAccountStatus | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(
    `${PLATFORM_SUPABASE_URL}/functions/v1/stripe-connect-status?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    },
  );

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to fetch Connect status');
  if (!json.connected) return null;
  return json;
}

// ── Downgrade / Update Subscription ──────────────────────────────────────────

/**
 * Downgrade a tenant's B2B plan.
 * - action='cancel': cancels at period end → free
 * - action='switch': switches to a lower paid plan with proration
 */
export async function updateSubscription(input: DowngradeInput): Promise<{ action: string; message?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/update-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      tenant_id: input.tenantId,
      target_plan_name: input.targetPlanName,
      action: input.action,
    }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update subscription');
  return { action: json.action, message: json.message };
}

// ── Business Leads (Platform-level) ──────────────────────────────────────────

export async function fetchBusinessLeads(): Promise<BusinessLead[]> {
  const { data, error } = await supabase
    .from('business_leads')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateBusinessLead(
  leadId: string,
  input: { status?: BusinessLeadStatus; admin_notes?: string | null },
): Promise<BusinessLead> {
  const { data, error } = await supabase
    .from('business_leads')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', leadId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Affiliate & Referral Program ────────────────────────────────────────────

// -- B2B Referral Codes --

export async function fetchReferralCode(tenantId: string): Promise<TenantReferralCode | null> {
  const { data, error } = await supabase
    .from('tenant_referral_codes')
    .select('*')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function generateReferralCode(tenantId: string, customCode?: string): Promise<TenantReferralCode> {
  const code = customCode || `REF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  const { data, error } = await supabase
    .from('tenant_referral_codes')
    .upsert({ tenant_id: tenantId, referral_code: code }, { onConflict: 'tenant_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateReferralCode(
  codeId: string,
  input: { referral_code?: string; commission_rate?: number; is_active?: boolean },
): Promise<TenantReferralCode> {
  const { data, error } = await supabase
    .from('tenant_referral_codes')
    .update(input)
    .eq('id', codeId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// -- B2B Referrals --

export async function fetchTenantReferrals(tenantId: string): Promise<TenantReferral[]> {
  const { data, error } = await supabase
    .from('tenant_referrals')
    .select('*, referred_tenant:tenants!referred_tenant_id(name, slug, status)')
    .eq('referrer_tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchReferralDashboardStats(tenantId: string): Promise<ReferralDashboardStats> {
  const [codeRes, referralsRes, commissionsRes, payoutsRes] = await Promise.all([
    supabase.from('tenant_referral_codes').select('commission_rate').eq('tenant_id', tenantId).maybeSingle(),
    supabase.from('tenant_referrals').select('id, status').eq('referrer_tenant_id', tenantId),
    supabase.from('referral_commissions').select('commission_amount, status').eq('referrer_tenant_id', tenantId),
    supabase.from('referral_payouts').select('amount, status').eq('referrer_tenant_id', tenantId),
  ]);

  const referrals = referralsRes.data || [];
  const commissions = commissionsRes.data || [];
  const payouts = payoutsRes.data || [];

  return {
    total_referrals: referrals.length,
    active_referrals: referrals.filter(r => r.status === 'active').length,
    total_earned: commissions.filter(c => c.status === 'earned' || c.status === 'paid').reduce((s, c) => s + c.commission_amount, 0),
    pending_payout: commissions.filter(c => c.status === 'earned').reduce((s, c) => s + c.commission_amount, 0)
      - payouts.filter(p => p.status === 'pending' || p.status === 'processing').reduce((s, p) => s + p.amount, 0),
    total_paid: payouts.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0),
    commission_rate: codeRes.data?.commission_rate || 15,
  };
}

// -- B2B Commissions --

export async function fetchReferralCommissions(tenantId: string): Promise<ReferralCommission[]> {
  const { data, error } = await supabase
    .from('referral_commissions')
    .select('*, referral:tenant_referrals(referred_tenant:tenants!referred_tenant_id(name))')
    .eq('referrer_tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// -- B2B Payouts (tenant view) --

export async function fetchReferralPayouts(tenantId: string): Promise<ReferralPayout[]> {
  const { data, error } = await supabase
    .from('referral_payouts')
    .select('*')
    .eq('referrer_tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// -- B2B Payouts (super admin) --

export async function fetchAllPayouts(status?: PayoutStatus): Promise<ReferralPayout[]> {
  let q = supabase
    .from('referral_payouts')
    .select('*, referrer_tenant:tenants!referrer_tenant_id(name, slug)')
    .order('created_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function fetchAllReferrals(): Promise<TenantReferral[]> {
  const { data, error } = await supabase
    .from('tenant_referrals')
    .select('*, referred_tenant:tenants!referred_tenant_id(name, slug, status)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function processReferralPayout(
  payoutId: string,
  updates: { status: PayoutStatus; admin_notes?: string; stripe_transfer_id?: string },
): Promise<ReferralPayout> {
  const { data, error } = await supabase
    .from('referral_payouts')
    .update({
      ...updates,
      processed_at: updates.status === 'paid' ? new Date().toISOString() : undefined,
    })
    .eq('id', payoutId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function createReferralPayout(
  referrerTenantId: string,
  amount: number,
): Promise<ReferralPayout> {
  const { data, error } = await supabase
    .from('referral_payouts')
    .insert({ referrer_tenant_id: referrerTenantId, amount })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchPlatformReferralStats(): Promise<{
  total_referrals: number; active_referrals: number;
  total_commissions_earned: number; pending_payouts: number; total_paid: number;
}> {
  const [refs, comms, payouts] = await Promise.all([
    supabase.from('tenant_referrals').select('id, status'),
    supabase.from('referral_commissions').select('commission_amount, status'),
    supabase.from('referral_payouts').select('amount, status'),
  ]);
  const referrals = refs.data || [];
  const commissions = comms.data || [];
  const pays = payouts.data || [];
  return {
    total_referrals: referrals.length,
    active_referrals: referrals.filter(r => r.status === 'active').length,
    total_commissions_earned: commissions.filter(c => c.status === 'earned' || c.status === 'paid').reduce((s, c) => s + c.commission_amount, 0),
    pending_payouts: pays.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0),
    total_paid: pays.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0),
  };
}

// -- Validate Referral Code (public, via edge function) --

export async function validateReferralCode(code: string): Promise<{ valid: boolean; referrer_tenant_name?: string }> {
  const res = await fetch(
    `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/validate-referral-code?code=${encodeURIComponent(code)}`,
    { headers: { 'apikey': (import.meta as any).env.VITE_SUPABASE_ANON_KEY } },
  );
  return res.json();
}

// ── Affiliate Partners (B2C booking links) ──────────────────────────────────

export async function fetchAffiliatePartners(tenantId: string): Promise<TenantAffiliatePartner[]> {
  const { data, error } = await supabase
    .from('tenant_affiliate_partners')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('partner_name');
  if (error) throw error;
  return data || [];
}

export async function upsertAffiliatePartner(
  tenantId: string,
  partner: { partner_key: string; partner_name: string; affiliate_id: string; is_active?: boolean },
): Promise<TenantAffiliatePartner> {
  const { data, error } = await supabase
    .from('tenant_affiliate_partners')
    .upsert(
      { tenant_id: tenantId, ...partner },
      { onConflict: 'tenant_id,partner_key' },
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteAffiliatePartner(partnerId: string, tenantId: string): Promise<void> {
  const { error } = await supabase
    .from('tenant_affiliate_partners')
    .delete()
    .eq('id', partnerId)
    .eq('tenant_id', tenantId);
  if (error) throw error;
}

// ── Curated Gear Products (B2C Gear & Essentials shelf hero picks) ───────────

export interface GearReview {
  author: string;
  rating: number | null;
  text: string;
}

export interface CuratedGearProduct {
  id?: string;
  tenant_id?: string;
  destination: string | null;
  product_name: string;
  brand: string | null;
  image_url: string | null;
  price: string | null;
  used_price: string | null;
  rating: number | null;
  review_count: number | null;
  testimonial: string | null;
  testimonial_author: string | null;
  reviews: GearReview[];
  why: string | null;
  buy_url: string;
  icon: string | null;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export async function fetchCuratedGear(tenantId: string): Promise<CuratedGearProduct[]> {
  const { data, error } = await supabase
    .from('curated_gear_products')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('sort_order')
    .order('created_at');
  if (error) throw error;
  return data || [];
}

export async function upsertCuratedGear(
  tenantId: string,
  product: Partial<CuratedGearProduct> & { product_name: string; buy_url: string },
): Promise<CuratedGearProduct> {
  const row: Record<string, unknown> = {
    tenant_id: tenantId,
    destination: product.destination ?? null,
    product_name: product.product_name,
    brand: product.brand ?? null,
    image_url: product.image_url ?? null,
    price: product.price ?? null,
    used_price: product.used_price ?? null,
    rating: product.rating ?? null,
    review_count: product.review_count ?? null,
    testimonial: product.testimonial ?? null,
    testimonial_author: product.testimonial_author ?? null,
    reviews: product.reviews ?? [],
    why: product.why ?? null,
    buy_url: product.buy_url,
    icon: product.icon ?? null,
    is_active: product.is_active ?? true,
    sort_order: product.sort_order ?? 0,
  };
  if (product.id) row.id = product.id;
  const { data, error } = await supabase
    .from('curated_gear_products')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCuratedGear(id: string, tenantId: string): Promise<void> {
  const { error } = await supabase
    .from('curated_gear_products')
    .delete()
    .eq('id', id)
    .eq('tenant_id', tenantId);
  if (error) throw error;
}

export async function fetchAffiliateClickStats(tenantId: string, days = 30): Promise<AffiliateClickStats> {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const weekAgo = new Date(Date.now() - 7 * 86400000);

  const { data: clicks, error } = await supabase
    .from('affiliate_click_events')
    .select('partner_key, source_type, created_at')
    .eq('tenant_id', tenantId)
    .gte('created_at', since);
  if (error) throw error;

  const all = clicks || [];
  const byPartner: Record<string, number> = {};
  const bySource: Record<string, number> = {};
  let clicksToday = 0, clicksWeek = 0;

  for (const c of all) {
    byPartner[c.partner_key] = (byPartner[c.partner_key] || 0) + 1;
    bySource[c.source_type] = (bySource[c.source_type] || 0) + 1;
    if (new Date(c.created_at) >= today) clicksToday++;
    if (new Date(c.created_at) >= weekAgo) clicksWeek++;
  }

  return {
    total_clicks: all.length,
    clicks_today: clicksToday,
    clicks_this_week: clicksWeek,
    clicks_this_month: all.length,
    by_partner: Object.entries(byPartner).map(([k, v]) => ({ partner_key: k, partner_name: k.replace(/_/g, '.'), clicks: v })),
    by_source: Object.entries(bySource).map(([k, v]) => ({ source_type: k, clicks: v })),
  };
}

// ── Affiliate Performance (per-day + per-bulletin breakdowns) ──────────────

export interface AffiliateClickByDay {
  day: string;     // ISO date
  clicks: number;
}

export interface AffiliateClickByBulletin {
  source_id: string;
  bulletin_title: string;
  partner_key: string;
  clicks: number;
  last_click: string;
}

export async function fetchAffiliateClicksByDay(
  tenantId: string,
  from: string,
  to: string,
): Promise<AffiliateClickByDay[]> {
  const { data, error } = await supabase.rpc('get_affiliate_clicks_by_day', {
    p_tenant_id: tenantId, p_from: from, p_to: to,
  });
  if (error) throw error;
  return (data ?? []).map((r: { day: string; clicks: number | string }) => ({
    day: r.day,
    clicks: Number(r.clicks) || 0,
  }));
}

export async function fetchAffiliateClicksByBulletin(
  tenantId: string,
  from: string,
  to: string,
  limit = 10,
): Promise<AffiliateClickByBulletin[]> {
  const { data, error } = await supabase.rpc('get_affiliate_clicks_by_bulletin', {
    p_tenant_id: tenantId, p_from: from, p_to: to, p_limit: limit,
  });
  if (error) throw error;
  return (data ?? []).map((r: AffiliateClickByBulletin & { clicks: number | string }) => ({
    ...r,
    clicks: Number(r.clicks) || 0,
  }));
}

// ── User Referral Config (B2C, Platform-side) ───────────────────────────────

export async function fetchTenantReferralConfig(tenantId: string): Promise<TenantReferralConfig | null> {
  const { data, error } = await supabase
    .from('tenant_referral_config')
    .select('*')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertTenantReferralConfig(
  tenantId: string,
  config: Partial<TenantReferralConfig>,
): Promise<TenantReferralConfig> {
  const { data, error } = await supabase
    .from('tenant_referral_config')
    .upsert({ tenant_id: tenantId, ...config }, { onConflict: 'tenant_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── User Referral Metrics (B2C, fetched from App DB) ─────────────────────────

export interface UserReferralMetrics {
  stats: {
    total_codes: number;
    active_codes: number;
    total_referrals: number;
    pending_referrals: number;
    completed_referrals: number;
    rewarded_referrals: number;
  };
  codes: {
    id: string;
    user_id: string;
    referral_code: string;
    is_active: boolean;
    total_referrals: number;
    created_at: string;
    referrer_email: string;
    referrer_name: string;
  }[];
  referrals: {
    id: string;
    referrer_user_id: string;
    referred_user_id: string;
    referral_code_used: string;
    status: string;
    referred_at: string;
    completed_at: string | null;
    created_at: string;
    referrer_email: string;
    referrer_name: string;
    referred_email: string;
    referred_name: string;
  }[];
}

export async function fetchUserReferralMetrics(tenantId: string): Promise<UserReferralMetrics | null> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    console.warn('[api] fetchUserReferralMetrics skipped — missing env vars');
    return null;
  }

  try {
    const res = await fetch(
      `${APP_SUPABASE_URL}/functions/v1/tenant-referrals?tenant_id=${encodeURIComponent(tenantId)}`,
      {
        method: 'GET',
        headers: { 'x-provision-secret': PROVISION_SECRET },
      },
    );

    const json = await res.json();
    if (!res.ok) {
      console.error('[api] tenant-referrals failed:', json);
      return null;
    }

    return {
      stats: json.stats,
      codes: json.codes ?? [],
      referrals: json.referrals ?? [],
    };
  } catch (err) {
    console.error('[api] fetchUserReferralMetrics error:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Platform Email Settings
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchPlatformEmailSettings(): Promise<PlatformEmailSettings | null> {
  const { data, error } = await supabase
    .from('platform_email_settings')
    .select('*')
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updatePlatformEmailSettings(
  updates: Partial<Pick<PlatformEmailSettings, 'default_from_name' | 'default_from_email' | 'default_reply_to' | 'email_footer_text'>>
): Promise<PlatformEmailSettings> {
  // Get the single row first
  const existing = await fetchPlatformEmailSettings();
  if (!existing) throw new Error('Platform email settings not found');

  const { data, error } = await supabase
    .from('platform_email_settings')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', existing.id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Platform AI Settings — which LLM provider runs generate-itinerary.
// Single-row table (platform_settings.id = 1). The column lives alongside
// other singleton platform-level flags. Super-admin flips it from
// /admin/ai-settings; the edge function reads it on each request.
// ═══════════════════════════════════════════════════════════════════════════════

export type ItineraryAIProvider = 'openai' | 'anthropic';
export type ItineraryParallelMode = 'per_city' | 'on' | 'off';
// Image-generation provider for the Trip Poster feature.
// Distinct from ItineraryAIProvider because the image fleet doesn't
// overlap with the LLM fleet — Anthropic doesn't render images, and
// Google's Imagen / fal.ai's Ideogram are usable image options without
// being LLMs.
//   'openai' — gpt-image-1 via api.openai.com (existing OPENAI_API_KEY)
//   'google' — Imagen 4 via generativelanguage.googleapis.com
//              (needs GOOGLE_AI_API_KEY in Supabase Secrets)
//   'fal'    — Ideogram V3 via fal.run (needs FAL_API_KEY in Supabase
//              Secrets). Best of the three at rendering titles
//              ("10 Days on the Alpine Riviera") legibly in-image.
export type PosterImageProvider = 'openai' | 'google' | 'fal';

export interface PlatformAISettings {
  id:                                 number;
  itinerary_ai_provider:              ItineraryAIProvider;
  itinerary_openai_model:             string;
  itinerary_anthropic_model:          string;
  itinerary_temperature_openai:       number;
  itinerary_temperature_anthropic:    number;
  itinerary_anthropic_max_tokens:     number;
  itinerary_parallel_mode:            ItineraryParallelMode;
  itinerary_brevity_mode:             boolean;
  // ── On-demand activity enrichment (enrich-activity edge function) ─
  // Independent from itinerary settings so super-admin can pick a
  // fast/cheap model for the UI-blocking enrichment popup while
  // keeping itinerary on a slower / more thorough model.
  enrichment_ai_provider:             ItineraryAIProvider;
  enrichment_openai_model:            string;
  enrichment_anthropic_model:         string;
  enrichment_temperature_openai:      number;
  enrichment_temperature_anthropic:   number;
  // ── Blitz refine (trip-chat edge function) ──
  // Independent from itinerary settings. Conversational refine on
  // Results — same shape as enrichment_* but a different LLM call.
  blitz_ai_provider:                  ItineraryAIProvider;
  blitz_openai_model:                 string;
  blitz_anthropic_model:              string;
  blitz_temperature_openai:           number;
  blitz_temperature_anthropic:        number;
  // ── Insider tab (generate-insider edge function) ──
  // Cultural etiquette / food / neighborhoods / local tips / seasonal
  // notes. Lazy-fetched on Insider tab open. Independent provider/
  // model/temp/max_tokens so super-admin can keep Insider on a faster
  // mid-range model (sub-30s target) while itinerary uses something
  // heavier.
  insider_ai_provider:                ItineraryAIProvider;
  insider_openai_model:               string;
  insider_anthropic_model:            string;
  insider_temperature_openai:         number;
  insider_temperature_anthropic:      number;
  insider_anthropic_max_tokens:       number;
  // ── Requirements tab (generate-trip-requirements edge function) ──
  // Pre-trip checklist: visa, health, practical. Factual content
  // (lower temperature). Independent so admin can pin a specific
  // model for compliance-style output.
  requirements_ai_provider:           ItineraryAIProvider;
  requirements_openai_model:          string;
  requirements_anthropic_model:       string;
  requirements_temperature_openai:    number;
  requirements_temperature_anthropic: number;
  requirements_anthropic_max_tokens:  number;
  // ── Journal Companion (generate-daily-journal + generate-trip-journal) ──
  // Narrative prose for daily entries + trip-level recaps. Independent
  // so super-admin can pick a tone-controllable model for storytelling
  // without affecting the rest of the AI fleet. Higher max_tokens
  // default (4000) because trip recaps regularly produce 4-8 paragraph
  // outputs plus per-day highlight blocks.
  journal_ai_provider:                ItineraryAIProvider;
  journal_openai_model:               string;
  journal_anthropic_model:            string;
  journal_temperature_openai:         number;
  journal_temperature_anthropic:      number;
  journal_anthropic_max_tokens:       number;
  // ── Trip Poster (generate-trip-poster edge function) ──
  // IMAGE generation, not LLM. Standalone provider field because
  // Anthropic doesn't render images and Google Imagen 4 is a viable
  // alternative without being an LLM. Default 'openai' (gpt-image-1).
  // Flipping to 'google' requires GOOGLE_AI_API_KEY in Supabase Secrets.
  poster_image_provider:              PosterImageProvider;
  // Per-provider model strings — free-text so the super-admin can flip
  // to gpt-image-2, FLUX, Recraft, or any future model the moment the
  // provider ships it, without a redeploy. The edge fn reads these on
  // each invocation (60s cache). Defaults match what the edge fn used
  // to hardcode.
  poster_openai_model:                string;
  poster_google_model:                string;
  poster_fal_model:                   string;
  // ── Trip Poster Marketplace (paid clones) ──
  // Super-admin controls the kill-switch, the price tier ladder, and
  // the author revenue share from one card. App reads these via the
  // get-poster-pricing-config edge fn (60s cached).
  poster_paid_clones_enabled:         boolean;
  /** Cents values the author may pick from at publish time. Must
   *  include 0 (Free) as the first element. Defaults [0,300,500,1000]
   *  = Free / $3 / $5 / $10. */
  poster_price_tiers_cents:           number[];
  /** Fraction 0..1 of paid-clone revenue routed to the author at
   *  PI-create time (snapshotted on poster_clone_payments.author_share_cents).
   *  Default 0.80 — creator-marketplace economics. */
  poster_author_revenue_share:        number;
  /** Custom-price input bounds — added by 20260627040000_poster_pricing_custom_bounds.
   *  Authors using the Custom chip on PosterGeneratePage can enter any
   *  value between these. Defaults: min 100 cents ($1), max 9900 cents
   *  ($99). UX-only — server doesn't enforce on publish (CHECK only
   *  requires price_tier_cents >= 0). */
  poster_min_custom_cents:            number;
  poster_max_custom_cents:            number;
  // Audit columns — when/by-whom each App-edge-function override
  // secret was last cleared via the AI Settings UI. NULL means
  // "never cleared via UI" (it may still have been cleared via
  // Supabase Dashboard or never set).
  itinerary_provider_secret_unset_at?:        string | null;
  itinerary_provider_secret_unset_by?:        string | null;
  itinerary_parallel_secret_unset_at?:        string | null;
  itinerary_parallel_secret_unset_by?:        string | null;
  itinerary_anthropic_model_secret_unset_at?: string | null;
  itinerary_anthropic_model_secret_unset_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

const AI_SETTINGS_COLUMNS =
  'id, itinerary_ai_provider, itinerary_openai_model, itinerary_anthropic_model, ' +
  'itinerary_temperature_openai, itinerary_temperature_anthropic, ' +
  'itinerary_anthropic_max_tokens, itinerary_parallel_mode, itinerary_brevity_mode, ' +
  'enrichment_ai_provider, enrichment_openai_model, enrichment_anthropic_model, ' +
  'enrichment_temperature_openai, enrichment_temperature_anthropic, ' +
  'blitz_ai_provider, blitz_openai_model, blitz_anthropic_model, ' +
  'blitz_temperature_openai, blitz_temperature_anthropic, ' +
  'insider_ai_provider, insider_openai_model, insider_anthropic_model, ' +
  'insider_temperature_openai, insider_temperature_anthropic, insider_anthropic_max_tokens, ' +
  'requirements_ai_provider, requirements_openai_model, requirements_anthropic_model, ' +
  'requirements_temperature_openai, requirements_temperature_anthropic, requirements_anthropic_max_tokens, ' +
  'journal_ai_provider, journal_openai_model, journal_anthropic_model, ' +
  'journal_temperature_openai, journal_temperature_anthropic, journal_anthropic_max_tokens, ' +
  'poster_image_provider, poster_openai_model, poster_google_model, poster_fal_model, ' +
  'poster_paid_clones_enabled, poster_price_tiers_cents, poster_author_revenue_share, ' +
  'poster_min_custom_cents, poster_max_custom_cents, ' +
  'itinerary_provider_secret_unset_at, itinerary_provider_secret_unset_by, ' +
  'itinerary_parallel_secret_unset_at, itinerary_parallel_secret_unset_by, ' +
  'itinerary_anthropic_model_secret_unset_at, itinerary_anthropic_model_secret_unset_by, ' +
  'updated_at, updated_by';

// ── App-secret management via manage-app-secrets edge function ───
// Wraps the Platform edge function that talks to the Supabase
// Management API to list / unset the three overridable App-project
// edge-function secrets:
//   ITINERARY_AI_PROVIDER, ITINERARY_PARALLEL_MODE,
//   ANTHROPIC_ITINERARY_MODEL

export type ManageableAppSecret =
  | 'ITINERARY_AI_PROVIDER'
  | 'ITINERARY_PARALLEL_MODE'
  | 'ANTHROPIC_ITINERARY_MODEL';

export interface AppSecretStatus {
  name: ManageableAppSecret;
  set:  boolean;
}

/** Lists which of the whitelisted App-edge-function secrets are
 *  currently set. Returns `configured: false` when the Platform
 *  edge function's PAT isn't configured (UI shows a "Not
 *  configured" state). */
export async function fetchAppSecretStatus(): Promise<{
  configured: boolean;
  secrets:    AppSecretStatus[];
  error?:     string;
}> {
  const { data, error } = await supabase.functions.invoke('manage-app-secrets', {
    body: { action: 'list' },
  });
  if (error) {
    // 503 = not configured (SUPABASE_ACCESS_TOKEN missing). Surface
    // as configured:false so the UI can show a helpful message.
    const status = (error as any)?.context?.status;
    if (status === 503) return { configured: false, secrets: [] };
    return { configured: false, secrets: [], error: error.message };
  }
  return {
    configured: data?.configured ?? false,
    secrets:    data?.secrets    ?? [],
    error:      data?.error,
  };
}

/** Deletes one whitelisted App-edge-function override secret. */
export async function unsetAppSecret(name: ManageableAppSecret): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('manage-app-secrets', {
    body: { action: 'unset', name },
  });
  if (error) return { ok: false, error: error.message };
  if (data?.error) return { ok: false, error: data.error };
  return { ok: !!data?.ok };
}

export async function fetchPlatformAISettings(): Promise<PlatformAISettings | null> {
  const { data, error } = await supabase
    .from('platform_settings')
    .select(AI_SETTINGS_COLUMNS)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as unknown as (PlatformAISettings | null);
}

/** Update any subset of the AI settings columns. Caller passes only the
 *  fields they're changing; everything else is left untouched on the row. */
export async function updatePlatformAISettings(
  updates: Partial<Omit<PlatformAISettings, 'id' | 'updated_at' | 'updated_by'>>
): Promise<PlatformAISettings> {
  // platform_settings is enforced singleton (id = 1) — fetch first
  // to honour the same pattern email-settings uses, even though we
  // know the id will always be 1.
  const existing = await fetchPlatformAISettings();
  if (!existing) throw new Error('Platform settings row not found');

  const { data, error } = await supabase
    .from('platform_settings')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', existing.id)
    .select(AI_SETTINGS_COLUMNS)
    .single();
  if (error) throw error;
  return data as unknown as PlatformAISettings;
}

// ── Model dropdown options ───────────────────────────────────────
// Hardcoded lists for the AI Settings UI. Adding a new model name
// here makes it available without a DB migration. The DB column is
// just a string, so any value the user types (or we add here)
// works — but the dropdown is the safer entry point since model
// name typos (e.g. 'claude-sonnet-4-5' vs the date-pinned variant
// that some accounts require) caused real production hangs.
export const ITINERARY_OPENAI_MODEL_OPTIONS: ReadonlyArray<{ value: string; label: string; hint?: string }> = [
  { value: 'gpt-4o',          label: 'gpt-4o',          hint: 'Default. Balanced.' },
  { value: 'gpt-4o-mini',     label: 'gpt-4o-mini',     hint: 'Fastest, cheapest.' },
  { value: 'gpt-4-turbo',     label: 'gpt-4-turbo',     hint: 'Older, slower.' },
];

export const ITINERARY_ANTHROPIC_MODEL_OPTIONS: ReadonlyArray<{ value: string; label: string; hint?: string }> = [
  // Top-tier narrative — Anthropic's most powerful tier (above Opus).
  // Premium use cases only: trip-publish journals, magazine-grade
  // insider essays, hero copy. $10/$50 per MTok is ~3× Opus, so reserve
  // for surfaces the user keeps or shares.
  { value: 'claude-fable-5',             label: 'claude-fable-5',             hint: 'Top tier for storytelling + narrative voice. 1M ctx. $10/$50 per MTok — use for keepsake journals, insider essays, hero prose.' },
  // Current-generation Opus — most capable general-purpose model.
  // Best for long-horizon agentic work, complex itinerary reasoning,
  // anywhere intelligence beats cost.
  { value: 'claude-opus-4-8',            label: 'claude-opus-4-8',            hint: 'Most capable general model. 1M ctx. $5/$25. Best for long-horizon agentic + complex reasoning.' },
  // Previous-gen Opus — same API surface as 4.8 (no breaking changes
  // moving between them). Pin here if a workload was tuned on 4.7 and
  // you want to defer the 4.8 re-tuning.
  { value: 'claude-opus-4-7',            label: 'claude-opus-4-7',            hint: 'Previous-gen Opus. 1M ctx. $5/$25. Same API surface as 4.8 — pin for tuned workloads.' },
  // Default for most features today — best speed/intelligence balance.
  // Verbose by default; pair with brevity instructions in the prompt.
  { value: 'claude-sonnet-4-6',          label: 'claude-sonnet-4-6',          hint: 'Best speed/intelligence balance. 1M ctx. $3/$15. Default for most features.' },
  // Production-stable date-pinned Sonnet 4.5 — kept for legacy callers
  // that explicitly need this exact build. Most callers should prefer
  // 4.6 above.
  { value: 'claude-sonnet-4-5-20250929', label: 'claude-sonnet-4-5-20250929', hint: 'Production-stable. Date-pinned legacy build.' },
  // Older Opus — still active but pre-4.7 API quirks (sampling params,
  // budget_tokens) are different. Prefer Opus 4.8 for new code.
  { value: 'claude-opus-4-6',            label: 'claude-opus-4-6',            hint: 'Older Opus. 1M ctx. Still active; prefer 4.8 for new code.' },
  // Legacy Opus — still active. Use only when a workload was carefully
  // tuned on this exact build.
  { value: 'claude-opus-4-5',            label: 'claude-opus-4-5',            hint: 'Legacy Opus. Still active. Prefer 4.8 for new code.' },
  // Fastest + cheapest tier. 200K context (not 1M). Good for parallel
  // mode, enrichment, anywhere latency matters more than depth.
  { value: 'claude-haiku-4-5',           label: 'claude-haiku-4-5',           hint: 'Fastest + cheapest. 200K ctx. $1/$5. Good in parallel mode + enrichment.' },
];

export const ITINERARY_PARALLEL_MODE_OPTIONS: ReadonlyArray<{ value: ItineraryParallelMode; label: string; hint: string }> = [
  { value: 'per_city', label: 'Per-city (N threads)',         hint: 'Fastest for multi-city. 1 trip-context + N per-city + 1 supplements calls.' },
  { value: 'on',       label: 'On (2-call split)',            hint: 'Default. Main itinerary + supplements in parallel.' },
  { value: 'off',      label: 'Off (legacy single call)',     hint: 'Revert target. Slowest. Use only if parallel modes misbehave.' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// Email Templates
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchEmailTemplates(): Promise<EmailTemplate[]> {
  const { data, error } = await supabase
    .from('email_templates')
    .select('*')
    .order('category')
    .order('display_name');
  if (error) throw error;
  return data || [];
}

export async function updateEmailTemplate(
  id: string,
  updates: Partial<Pick<EmailTemplate, 'subject_template' | 'cta_text' | 'cta_color' | 'footer_text' | 'is_platform_enabled' | 'default_heading' | 'default_subtitle' | 'default_body' | 'available_variables'>>
): Promise<EmailTemplate> {
  const { data, error } = await supabase
    .from('email_templates')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function toggleEmailTemplate(id: string, enabled: boolean): Promise<void> {
  const { error } = await supabase
    .from('email_templates')
    .update({ is_platform_enabled: enabled, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tenant Email Settings (for tenant admin Email page)
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchTenantEmailSettings(tenantId: string): Promise<{
  email_from_name: string | null;
  email_from_address: string | null;
  email_reply_to: string | null;
  notification_prefs: Record<string, boolean>;
}> {
  const [brandingResult, tenantResult] = await Promise.all([
    supabase
      .from('branding_configs')
      .select('email_from_name, email_from_address, email_reply_to')
      .eq('tenant_id', tenantId)
      .maybeSingle(),
    supabase
      .from('tenants')
      .select('notification_prefs')
      .eq('id', tenantId)
      .maybeSingle(),
  ]);

  if (brandingResult.error) throw brandingResult.error;
  if (tenantResult.error) throw tenantResult.error;

  return {
    email_from_name: brandingResult.data?.email_from_name || null,
    email_from_address: brandingResult.data?.email_from_address || null,
    email_reply_to: brandingResult.data?.email_reply_to || null,
    notification_prefs: tenantResult.data?.notification_prefs || {},
  };
}

export async function updateTenantEmailIdentity(
  tenantId: string,
  updates: { email_from_name?: string | null; email_from_address?: string | null; email_reply_to?: string | null }
): Promise<void> {
  const { error } = await supabase
    .from('branding_configs')
    .update(updates)
    .eq('tenant_id', tenantId);
  if (error) throw error;
}

export async function updateNotificationPrefs(
  tenantId: string,
  prefs: Record<string, boolean>
): Promise<void> {
  const { error } = await supabase
    .from('tenants')
    .update({ notification_prefs: prefs })
    .eq('id', tenantId);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tenant Email Templates (per-tenant content overrides)
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchTenantEmailTemplates(tenantId: string): Promise<TenantEmailTemplate[]> {
  const { data, error } = await supabase
    .from('tenant_email_templates')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('template_key');
  if (error) throw error;
  return data || [];
}

export async function upsertTenantEmailTemplate(
  tenantId: string,
  templateKey: string,
  updates: UpsertTenantEmailTemplateInput
): Promise<TenantEmailTemplate> {
  const { data, error } = await supabase
    .from('tenant_email_templates')
    .upsert(
      {
        tenant_id: tenantId,
        template_key: templateKey,
        ...updates,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'tenant_id,template_key' }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteTenantEmailTemplate(
  tenantId: string,
  templateKey: string
): Promise<void> {
  const { error } = await supabase
    .from('tenant_email_templates')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('template_key', templateKey);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Campaign Emails
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchCampaigns(tenantId: string): Promise<CampaignEmail[]> {
  const { data, error } = await supabase
    .from('campaign_emails')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchCampaign(id: string): Promise<CampaignEmail> {
  const { data, error } = await supabase
    .from('campaign_emails')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export async function createCampaign(
  tenantId: string,
  input: CreateCampaignInput
): Promise<CampaignEmail> {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('campaign_emails')
    .insert({
      tenant_id: tenantId,
      subject: input.subject,
      heading: input.heading ?? null,
      body: input.body,
      cta_text: input.cta_text ?? null,
      cta_url: input.cta_url ?? null,
      recipient_filter: input.recipient_filter ?? 'all',
      scheduled_at: input.scheduled_at ?? null,
      status: input.scheduled_at ? 'scheduled' : 'draft',
      created_by: user?.id ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateCampaign(
  id: string,
  updates: Partial<CreateCampaignInput>
): Promise<CampaignEmail> {
  const { data, error } = await supabase
    .from('campaign_emails')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function cancelCampaign(id: string): Promise<void> {
  const { error } = await supabase
    .from('campaign_emails')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function sendCampaignNow(campaignId: string): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const { data: { session } } = await supabase.auth.getSession();

  const res = await fetch(`${supabaseUrl}/functions/v1/send-campaign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || anonKey}`,
    },
    body: JSON.stringify({ campaign_id: campaignId }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send campaign' }));
    throw new Error(err.error || 'Failed to send campaign');
  }
}

export async function fetchCampaignRecipients(campaignId: string): Promise<CampaignRecipient[]> {
  const { data, error } = await supabase
    .from('campaign_recipients')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('created_at');
  if (error) throw error;
  return data || [];
}

export async function fetchCampaignsThisMonth(tenantId: string): Promise<number> {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const { count, error } = await supabase
    .from('campaign_emails')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .in('status', ['sent', 'sending', 'scheduled'])
    .gte('created_at', startOfMonth);
  if (error) throw error;
  return count || 0;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Platform Announcements
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchPlatformAnnouncements(): Promise<PlatformAnnouncement[]> {
  const { data, error } = await supabase
    .from('platform_announcements')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchActivePlatformAnnouncements(): Promise<PlatformAnnouncement[]> {
  const { data, error } = await supabase
    .from('platform_announcements')
    .select('*')
    .eq('is_active', true)
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createPlatformAnnouncement(
  input: CreatePlatformAnnouncementInput
): Promise<PlatformAnnouncement> {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('platform_announcements')
    .insert({ ...input, created_by: user?.id })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updatePlatformAnnouncement(
  id: string,
  updates: UpdatePlatformAnnouncementInput
): Promise<PlatformAnnouncement> {
  const { data, error } = await supabase
    .from('platform_announcements')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deletePlatformAnnouncement(id: string): Promise<void> {
  const { error } = await supabase
    .from('platform_announcements')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function sendPlatformAnnouncementEmail(announcementId: string): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const { data: { session } } = await supabase.auth.getSession();

  const res = await fetch(`${supabaseUrl}/functions/v1/send-platform-announcement`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || anonKey}`,
    },
    body: JSON.stringify({ announcement_id: announcementId }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send' }));
    throw new Error(err.error || 'Failed to send announcement email');
  }
}


// ── Discovery interviews (customer-discovery log) ─────────────────────────────

export async function fetchDiscoveryInterviews(): Promise<DiscoveryInterview[]> {
  const { data, error } = await supabase
    .from('discovery_interviews')
    .select('*')
    .order('interview_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createDiscoveryInterview(
  input: CreateDiscoveryInterviewInput
): Promise<DiscoveryInterview> {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('discovery_interviews')
    .insert({ ...input, created_by: user?.id })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateDiscoveryInterview(
  id: string,
  updates: UpdateDiscoveryInterviewInput
): Promise<DiscoveryInterview> {
  const { data, error } = await supabase
    .from('discovery_interviews')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteDiscoveryInterview(id: string): Promise<void> {
  const { error } = await supabase
    .from('discovery_interviews')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/** Bulk-insert interviews (e.g. from an Excel/CSV import). */
export async function createDiscoveryInterviews(
  inputs: CreateDiscoveryInterviewInput[]
): Promise<DiscoveryInterview[]> {
  if (inputs.length === 0) return [];
  const { data: { user } } = await supabase.auth.getUser();
  const rows = inputs.map(i => ({ ...i, created_by: user?.id }));
  const { data, error } = await supabase
    .from('discovery_interviews')
    .insert(rows)
    .select();
  if (error) throw error;
  return data || [];
}

/** Total recorded visits to the public /survey page. */
export async function fetchSurveyViewCount(): Promise<number> {
  const { count, error } = await supabase
    .from('survey_views')
    .select('*', { count: 'exact', head: true });
  if (error) throw error;
  return count || 0;
}

/** Run the LLM synthesis over the logged interviews (names omitted). Returns markdown. */
export async function analyzeDiscoveryInterviews(rows: DiscoveryInterview[]): Promise<string> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const interviews = rows.map((r, i) => ({
    n: i + 1,
    date: r.interview_date,
    source: r.source,
    persona: r.persona,
    channel: r.channel,
    last_trip: r.last_trip,
    pain: r.pain,
    wedge: r.wedge,
    tools: r.tools,
    paid: r.paid,
    referral: r.referral,
    takeaway: r.takeaway,
    notes: r.notes,
    age_group: r.age_group,
    ethnicity: r.ethnicity,
  }));

  const res = await fetch(`${url}/functions/v1/analyze-interviews`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ interviews }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Analysis failed' }));
    throw new Error(err.error || 'Analysis failed');
  }
  const data = await res.json();
  return data.analysis || '';
}


// ── Plans CRUD (Plan Builder) ────────────────────────────────────────────────

/**
 * Auto-create / update Stripe Product + recurring Prices for a B2B plan.
 * Called after createPlan or updatePlan when the plan has a non-zero price.
 * Non-fatal: logs a warning on failure so the save still succeeds.
 */
export async function syncB2BPlanStripe(
  planId: string,
  planData: { name: string; display_name: string; price_per_seat_monthly: number; price_per_seat_annual: number },
): Promise<{ stripe_product_id: string; stripe_price_id_monthly: string; stripe_price_id_annual: string } | null> {
  try {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) {
      console.warn('[syncB2BPlanStripe] No auth session — skipping Stripe sync');
      return null;
    }

    const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/manage-b2b-plans`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'sync_stripe',
        plan_id: planId,
        plan_name: planData.name,
        display_name: planData.display_name,
        price_monthly_cents: planData.price_per_seat_monthly,
        price_annual_cents: planData.price_per_seat_annual,
      }),
    });

    const json = await res.json();
    if (!res.ok) {
      console.warn('[syncB2BPlanStripe] Stripe sync failed:', json.error || json);
      return null;
    }

    console.log('[syncB2BPlanStripe] Synced:', json);
    return json;
  } catch (err) {
    console.warn('[syncB2BPlanStripe] Error:', (err as Error).message);
    return null;
  }
}

export async function createPlan(input: CreatePlanInput): Promise<TenantPlan> {
  const { data, error } = await supabase
    .from('tenant_plans')
    .insert({
      name: input.name,
      display_name: input.display_name,
      description: input.description || '',
      color: input.color || '#64748b',
      rank: input.rank ?? 0,
      price_per_seat_monthly: input.price_per_seat_monthly,
      price_per_seat_annual: input.price_per_seat_annual,
      max_users: input.max_users,
      max_trips_per_month: input.max_trips_per_month,
      max_storage_mb: input.max_storage_mb,
      max_campaigns_per_month: input.max_campaigns_per_month,
      max_campaign_recipients: input.max_campaign_recipients,
      max_b2c_plans: input.max_b2c_plans,
      max_b2c_free_plans: input.max_b2c_free_plans,
      stripe_price_id_monthly: input.stripe_price_id_monthly || '',
      stripe_price_id_annual: input.stripe_price_id_annual || '',
      is_active: true,
    })
    .select()
    .single();
  if (error) throw error;

  // Auto-sync to Stripe if plan has a price (non-blocking, non-fatal)
  if (data.price_per_seat_monthly > 0 || data.price_per_seat_annual > 0) {
    const stripeResult = await syncB2BPlanStripe(data.id, data);
    if (stripeResult) {
      // Update local object with new Stripe IDs
      data.stripe_product_id = stripeResult.stripe_product_id;
      data.stripe_price_id_monthly = stripeResult.stripe_price_id_monthly;
      data.stripe_price_id_annual = stripeResult.stripe_price_id_annual;
    }
  }

  return data;
}

export async function updatePlan(
  id: string,
  updates: Partial<Omit<TenantPlan, 'id'>>,
): Promise<TenantPlan> {
  const { data, error } = await supabase
    .from('tenant_plans')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;

  // Propagate usage limits to all tenants on this plan (non-fatal)
  propagatePlanLimitsToTenants(id, data).catch(err =>
    console.warn('[updatePlan] limit propagation failed:', err?.message || err),
  );

  // Auto-sync to Stripe if plan has a price (non-blocking, non-fatal)
  if (data.price_per_seat_monthly > 0 || data.price_per_seat_annual > 0) {
    const stripeResult = await syncB2BPlanStripe(id, data);
    if (stripeResult) {
      data.stripe_product_id = stripeResult.stripe_product_id;
      data.stripe_price_id_monthly = stripeResult.stripe_price_id_monthly;
      data.stripe_price_id_annual = stripeResult.stripe_price_id_annual;
    }
  }

  return data;
}

/**
 * When a plan's usage limits change, sync max_users and storage_limit_mb
 * to every tenant currently on that plan so they reflect the latest values.
 */
async function propagatePlanLimitsToTenants(
  planId: string,
  plan: TenantPlan,
): Promise<void> {
  const { data: tenants, error: tErr } = await supabase
    .from('tenants')
    .select('id')
    .eq('plan_id', planId);
  if (tErr) { console.warn('[propagateLimits] failed to list tenants:', tErr.message); return; }
  if (!tenants || tenants.length === 0) return;

  const update: Record<string, unknown> = {};
  if (plan.max_users !== undefined) update.max_users = plan.max_users ?? 999;
  if (plan.max_storage_mb !== undefined) update.storage_limit_mb = plan.max_storage_mb;

  if (Object.keys(update).length === 0) return;

  const { error: uErr } = await supabase
    .from('tenants')
    .update(update)
    .in('id', tenants.map(t => t.id));
  if (uErr) {
    console.warn('[propagateLimits] failed to update tenants:', uErr.message);
  } else {
    console.log(`[propagateLimits] synced ${tenants.length} tenant(s) with plan ${planId}`);
  }
}

export async function archivePlan(id: string): Promise<void> {
  const { error } = await supabase
    .from('tenant_plans')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Hard-delete a plan and ALL tenants assigned to it (cascade).
 *
 * Order of operations:
 *   1. Find all tenants on this plan
 *   2. Delete each tenant fully (App DB + Platform DB + DNS cleanup)
 *   3. Delete the plan itself (plan_feature_defaults auto-cascade)
 *   4. Audit log
 *
 * Returns the count of tenants that were deleted.
 */
export async function deletePlanFull(planId: string): Promise<{ deletedTenants: number }> {
  // Step 1: Find all tenants on this plan
  const { data: affectedTenants, error: fetchErr } = await supabase
    .from('tenants')
    .select('id, name')
    .eq('plan_id', planId);

  if (fetchErr) throw fetchErr;

  // Step 2: Delete each tenant fully (App DB data + Platform DB cascade)
  const tenantIds = (affectedTenants || []).map(t => t.id);
  for (const tenantId of tenantIds) {
    await deleteTenantFull(tenantId);
  }

  // Step 3: Delete the plan itself (plan_feature_defaults has ON DELETE CASCADE)
  const { error: delErr } = await supabase
    .from('tenant_plans')
    .delete()
    .eq('id', planId);

  if (delErr) throw delErr;

  // Step 4: Audit
  logAudit(null, 'plan_deleted', 'plan', planId, {
    deleted_tenants: tenantIds.length,
  }).catch(() => {});

  return { deletedTenants: tenantIds.length };
}

/**
 * Returns how many tenants are currently assigned to a given plan.
 * Used by the UI to warn before deletion.
 */
export async function countTenantsOnPlan(planId: string): Promise<number> {
  const { count, error } = await supabase
    .from('tenants')
    .select('id', { count: 'exact', head: true })
    .eq('plan_id', planId);
  if (error) throw error;
  return count ?? 0;
}


// ── Plan Feature Defaults ────────────────────────────────────────────────────

export async function fetchPlanFeatureDefaults(planId: string): Promise<PlanFeatureDefault[]> {
  const { data, error } = await supabase
    .from('plan_feature_defaults')
    .select('*')
    .eq('plan_id', planId)
    .order('feature_key');
  if (error) throw error;
  return data ?? [];
}

/**
 * Keys that exist as columns in the feature_configs table.
 * Used to filter out B2C-only keys (max_active_trips, geofencing, etc.)
 * that live in plan_feature_defaults but NOT in feature_configs.
 */
const FEATURE_CONFIG_COLUMNS = new Set([
  'ai_trip_builder', 'budget_manager', 'expense_splitting', 'live_dashboard',
  'voice_interpreter', 'cultural_hub', 'packing_list', 'receipt_ocr',
  'collaboration', 'local_agent', 'zoom_meetings', 'offline_mode',
  'api_access', 'sso_saml', 'custom_branding', 'custom_domain',
  'doc_scanner', 'visual_context', 'smart_companion', 'email_campaigns',
  'content_blog_feed', 'content_youtube', 'content_instagram',
  'bulletin_youtube', 'bulletin_listings', 'branding_destinations', 'branding_info_cards',
]);

/** Returns a flat { feature_key: is_enabled } map — filtered to feature_configs columns only */
export async function fetchPlanFeatureDefaultsMap(
  planId: string,
): Promise<Record<string, boolean>> {
  const rows = await fetchPlanFeatureDefaults(planId);
  const map: Record<string, boolean> = {};
  for (const r of rows) {
    if (FEATURE_CONFIG_COLUMNS.has(r.feature_key)) {
      map[r.feature_key] = r.is_enabled;
    }
  }
  return map;
}

/**
 * Manually re-run the Platform → App sync for one Platform plan. Reads
 * the current plan_feature_defaults rows + the tenants on that plan,
 * then fires sync_ceiling per tenant via the existing helper used by
 * upsertPlanFeatureDefaults. Surfaces per-tenant errors so the UI can
 * show success/failure counts in a toast.
 *
 * Used by the "Sync to tenants" button on PlatformPlansTab — gives
 * the operator an explicit retry path when the auto-trigger missed,
 * the Vault secrets aren't seeded yet, or a tenant's sync failed.
 */
export async function syncPlanFeaturesToTenants(planId: string): Promise<{
  attempted: number;
  succeeded: number;
  failed:    Array<{ tenantId: string; error: string }>;
}> {
  // Load the current default rows so we know which features are
  // allowed for this plan right now.
  const { data: defaults, error: dErr } = await supabase
    .from('plan_feature_defaults')
    .select('feature_key, is_enabled')
    .eq('plan_id', planId);
  if (dErr) throw new Error(`Failed to load plan_feature_defaults: ${dErr.message}`);

  const allowedKeys = (defaults ?? [])
    .filter(r => r.is_enabled)
    .map(r => r.feature_key as string);

  // Find tenants on this plan.
  const { data: tenants, error: tErr } = await supabase
    .from('tenants')
    .select('id')
    .eq('plan_id', planId);
  if (tErr) throw new Error(`Failed to load tenants: ${tErr.message}`);

  const failed: Array<{ tenantId: string; error: string }> = [];
  let succeeded = 0;

  // Fire one syncB2BCeilingToB2C per tenant. The existing helper hits
  // the App-DB edge function with x-provision-secret.
  for (const t of tenants ?? []) {
    try {
      await syncB2BCeilingToB2C(t.id, allowedKeys);
      succeeded += 1;
    } catch (e: any) {
      failed.push({ tenantId: t.id, error: e?.message || String(e) });
    }
  }

  return { attempted: tenants?.length ?? 0, succeeded, failed };
}

/** Bulk replace all feature defaults for a plan */
export async function upsertPlanFeatureDefaults(
  planId: string,
  features: Record<string, boolean>,
  limits?: Record<string, number | null>,
): Promise<void> {
  // Delete existing then insert — simplest approach for bulk replace
  await supabase.from('plan_feature_defaults').delete().eq('plan_id', planId);

  const rows = Object.entries(features).map(([feature_key, is_enabled]) => ({
    plan_id: planId,
    feature_key,
    is_enabled,
    limit_value: limits?.[feature_key] ?? null,
  }));

  if (rows.length > 0) {
    const { error } = await supabase.from('plan_feature_defaults').insert(rows);
    if (error) throw error;
  }

  // Propagate to all existing tenants on this plan so their feature_configs stay in sync
  await propagatePlanFeaturesToTenants(planId, features);
}

/**
 * When a plan's feature defaults change, update the feature_configs for
 * every tenant currently on that plan so the B2C ceiling stays in sync.
 *
 * Two-pronged sync:
 *   1. Platform DB: bulk-update feature_configs (column-based legacy system)
 *   2. App DB:      call syncB2BCeilingToB2C per tenant so ceiling_enabled
 *                   is set on tenant_plan_features (blocks B2C end-users)
 *
 * Non-fatal — logs warnings but doesn't throw (plan save shouldn't fail).
 */
async function propagatePlanFeaturesToTenants(
  planId: string,
  features: Record<string, boolean>,
): Promise<void> {
  try {
    // Find all tenants on this plan
    const { data: tenants, error: tErr } = await supabase
      .from('tenants')
      .select('id')
      .eq('plan_id', planId);
    if (tErr) { console.warn('[propagate] failed to list tenants:', tErr.message); return; }
    if (!tenants || tenants.length === 0) return;

    // ── 1. Platform DB: bulk-update feature_configs ───────────────────
    const update: Record<string, boolean> = {};
    for (const [key, val] of Object.entries(features)) {
      if (typeof val === 'boolean' && FEATURE_CONFIG_COLUMNS.has(key)) update[key] = val;
    }

    const { error: uErr } = await supabase
      .from('feature_configs')
      .update(update)
      .in('tenant_id', tenants.map(t => t.id));
    if (uErr) {
      console.warn('[propagate] failed to update feature_configs:', uErr.message);
    } else {
      console.log(`[propagate] synced ${tenants.length} tenant(s) feature_configs with plan ${planId}`);
    }

    // ── 2. App DB: sync ceiling_enabled on tenant_plan_features ───────
    // Build list of allowed B2C feature keys (features that are enabled)
    const allowedKeys = Object.entries(features)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);

    // Fire-and-forget per tenant — don't block PlanBuilder save
    for (const tenant of tenants) {
      syncB2BCeilingToB2C(tenant.id, allowedKeys).catch(err => {
        console.warn(`[propagate] B2C ceiling sync failed for tenant ${tenant.id}:`, err?.message || err);
      });
    }
    console.log(`[propagate] initiated B2C ceiling sync for ${tenants.length} tenant(s)`);
  } catch (err: any) {
    console.warn('[propagate] unexpected error:', err?.message || err);
  }
}

// ── Sync Pipeline ─────────────────────────────────────────────────────────────

export interface SyncLogEntry {
  id: string;
  sync_type: string;
  status: 'success' | 'partial' | 'failed';
  tenants_synced: number;
  tenants_failed: number;
  details: Record<string, unknown>;
  triggered_by: 'cron' | 'manual' | 'sql';
  created_at: string;
}

export interface PlatformNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  read: boolean;
  created_at: string;
}

/**
 * Trigger sync-usage-metrics edge function manually.
 * Optionally pass a tenant_id to sync a single tenant.
 */
export async function triggerUsageSync(tenantId?: string): Promise<{ ok: boolean; sync_status: string; synced: number; failed: number }> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const body: Record<string, string> = {};
  if (tenantId) body.tenant_id = tenantId;

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/sync-usage-metrics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Sync failed');
  return json;
}

// ── Dashboard extras (top users + AI error rate) ────────────────────────────

export interface TopUserEntry {
  user_id: string;
  email: string | null;
  full_name: string | null;
  total_activity: number;
  ai_calls: number;
  trips_touched: number;
}

export interface AiErrorStats {
  success_count: number;
  error_count: number;
  error_rate: number;          // percent, 0-100
}

export interface AiByFeatureEntry {
  feature: string;
  calls: number;
  errors: number;
}

/** A single row of the AI Calls modal's "All users" table. Returned by
 *  the App DB's get_ai_active_users_for_tenant RPC (every user with at
 *  least one AI call in the window, capped at 200 server-side). No
 *  combined "total_activity" score — the modal ranks by ai_calls and
 *  shows trips_touched separately so neither number is ambiguous. */
export interface AiActiveUserEntry {
  user_id:       string;
  email:         string;
  full_name:     string;
  ai_calls:      number;
  trips_touched: number;
}

export interface DashboardExtras {
  ok: boolean;
  top_users: TopUserEntry[];
  ai_errors: AiErrorStats;
  ai_by_feature: AiByFeatureEntry[];
  /** Every user with AI activity in the window, sorted by ai_calls
   *  desc. Powers the AI Calls modal's all-users table. Optional in
   *  the type because the App DB edge function may not be on the new
   *  shape yet right after a partial deploy. */
  ai_active_users?: AiActiveUserEntry[];
}

/** Fetch top users + AI error rate for a tenant over [from, to] (YYYY-MM-DD). */
export async function fetchDashboardExtras(
  tenantId: string,
  from: string,
  to: string,
): Promise<DashboardExtras> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(`${PLATFORM_SUPABASE_URL}/functions/v1/fetch-dashboard-extras`, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ tenant_id: tenantId, from, to }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to fetch dashboard extras');
  return json as DashboardExtras;
}

/** Fetch recent sync log entries */
export async function fetchSyncLog(limit = 20): Promise<SyncLogEntry[]> {
  const { data, error } = await supabase
    .from('sync_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

/** Fetch unread platform notifications */
export async function fetchNotifications(unreadOnly = true): Promise<PlatformNotification[]> {
  let q = supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);
  if (unreadOnly) q = q.eq('read', false);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/** Mark a notification as read */
export async function markNotificationRead(id: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', id);
  if (error) throw error;
}

/** Mark all notifications as read */
export async function markAllNotificationsRead(): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('read', false);
  if (error) throw error;
}

// ── Cron Schedule Management ──────────────────────────────────────────────────

export interface CronSchedule {
  jobname: string;
  schedule: string;
  active: boolean;
  jobid: number;
}

/** Fetch the current cron schedule for a named job */
export async function fetchCronSchedule(
  jobname = 'daily-usage-metrics-sync'
): Promise<CronSchedule> {
  const { data, error } = await supabase.rpc('get_cron_schedule', {
    p_jobname: jobname,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as CronSchedule;
}

/** Update the cron schedule for a named job */
export async function updateCronSchedule(
  newSchedule: string,
  jobname = 'daily-usage-metrics-sync'
): Promise<{ success: boolean; new_schedule: string }> {
  const { data, error } = await supabase.rpc('update_cron_schedule', {
    p_jobname: jobname,
    p_schedule: newSchedule,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

// ── Data retention policies (cross-DB to App's data_retention_policies) ─────
//
// All three helpers call the App DB edge function `admin-retention-policies`
// using the same x-provision-secret pattern as provision-tenant /
// update-tenant-pricing. Reads return the platform-default rows + (when
// tenantId is given) that tenant's overrides; writes go through the
// edge function which enforces the role guard (tenant_admin can't edit
// the platform default) and writes the audit log.

export interface RetentionPolicyRow {
  id:             string;
  tenant_id:      string | null;     // null = platform default
  artifact_type:  string;
  retention_days: number | null;     // null = keep forever
  enabled:        boolean;
  set_by_role:    'super_admin' | 'tenant_admin';
  notes:          string | null;
  updated_at:     string;
}

async function callRetentionFn<T>(body: Record<string, unknown>): Promise<T> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('App DB call skipped — missing VITE_APP_SUPABASE_URL or VITE_PROVISION_SECRET');
  }
  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/admin-retention-policies`, {
    method:  'POST',
    headers: {
      'Content-Type':       'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok || json.error) {
    throw new Error(json.error || `admin-retention-policies returned ${res.status}`);
  }
  return json as T;
}

/**
 * List policies. Pass tenantId to get platform defaults + that tenant's
 * overrides; pass null/undefined to get just the platform defaults
 * (super-admin view).
 */
export async function listRetentionPolicies(
  tenantId: string | null,
): Promise<RetentionPolicyRow[]> {
  const res = await callRetentionFn<{ ok: true; rows: RetentionPolicyRow[] }>({
    op:        'list',
    tenant_id: tenantId,
  });
  return res.rows;
}

/**
 * Upsert one policy row. Pass tenant_id=null + set_by_role='super_admin'
 * to edit the platform default; pass tenant_id=<id> + set_by_role=
 * 'tenant_admin' to set/update a tenant override. The edge function
 * rejects mismatched combinations (e.g., tenant_admin trying to edit
 * the platform default).
 */
export async function upsertRetentionPolicy(args: {
  tenant_id:      string | null;
  artifact_type:  string;
  retention_days: number | null;
  set_by_role:    'super_admin' | 'tenant_admin';
  set_by_email:   string;
  notes?:         string | null;
}): Promise<RetentionPolicyRow> {
  const res = await callRetentionFn<{ ok: true; row: RetentionPolicyRow }>({
    op: 'upsert',
    ...args,
  });
  return res.row;
}

/** Remove a policy row. For tenants this resets resolution to the
 *  platform default. */
export async function deleteRetentionPolicy(args: {
  tenant_id:    string | null;
  artifact_type: string;
  set_by_role:  'super_admin' | 'tenant_admin';
  set_by_email: string;
}): Promise<{ deleted: number }> {
  const res = await callRetentionFn<{ ok: true; deleted: number }>({
    op: 'delete',
    ...args,
  });
  return { deleted: res.deleted };
}

// ── Anonymous-user retention (platform_settings) ─────────────────────────────
// These talk to the singleton public.platform_settings row that stores the
// platform-wide default retention for anonymous "guest" accounts (i.e.
// Supabase auth users created via signInAnonymously). Tenant-specific
// overrides live on branding_configs.anon_retention_days_override and are
// edited through the existing updateBranding() pathway — there's no separate
// helper here because the branding row already exposes the column.
//
// The cron that actually deletes expired guests is a separate concern; this
// just lets admins configure the contract.

export interface PlatformSettingsRow {
  id:                          number;
  anon_retention_days:         number;
  /** Platform-wide cap on anonymous signups from a single hashed IP
   *  per 24h. Default 3. */
  anon_signups_per_ip_per_day: number;
  /** Whether the FeedbackModal renders on tenant home pages by default.
   *  Tenants can override per-tenant via branding_configs. */
  feedback_enabled:            boolean;
  /** UTC hour-of-day (0..23) the anonymous-user cleanup cron runs for
   *  tenants that haven't set their own override. Default 4 (04:00 UTC).
   *  Tenants override via branding_configs.anon_cleanup_hour_utc_override. */
  anon_cleanup_hour_utc:       number;
  updated_at:                  string;
  updated_by:                  string | null;
}

/** Read the singleton platform_settings row (id = 1). Returns null if the
 *  table hasn't been migrated yet so the caller can fall back to a sane
 *  default rather than blowing up the page. */
export async function fetchPlatformSettings(): Promise<PlatformSettingsRow | null> {
  const { data, error } = await supabase
    .from('platform_settings')
    .select('id, anon_retention_days, anon_signups_per_ip_per_day, feedback_enabled, anon_cleanup_hour_utc, updated_at, updated_by')
    .eq('id', 1)
    .maybeSingle();
  if (error) {
    // 42P01 = undefined_table — happens on dev DBs that haven't run the
    // migration yet. Surface as "no settings" instead of a hard error.
    if ((error as { code?: string }).code === '42P01') return null;
    // 42703 = undefined_column — happens when one of the optional
    // columns (anon_signups_per_ip_per_day or anon_cleanup_hour_utc)
    // hasn't been added yet. Re-fetch with the minimal column set and
    // backfill the missing ones with sane defaults so the page still
    // renders.
    if ((error as { code?: string }).code === '42703') {
      const fallback = await supabase
        .from('platform_settings')
        .select('id, anon_retention_days, updated_at, updated_by')
        .eq('id', 1)
        .maybeSingle();
      if (fallback.error) {
        if ((fallback.error as { code?: string }).code === '42P01') return null;
        throw new Error(fallback.error.message || 'Failed to read platform_settings');
      }
      return fallback.data
        ? {
            ...(fallback.data as Omit<PlatformSettingsRow, 'anon_signups_per_ip_per_day' | 'feedback_enabled' | 'anon_cleanup_hour_utc'>),
            anon_signups_per_ip_per_day: 3,
            feedback_enabled:            true,
            anon_cleanup_hour_utc:       4,
          }
        : null;
    }
    throw new Error(error.message || error.code || 'Failed to read platform_settings');
  }
  return data as PlatformSettingsRow | null;
}

/** Toggle the platform-wide feedback-enabled default. Super admin only. */
export async function updateFeedbackEnabledDefault(
  enabled:   boolean,
  updatedBy: string | null,
): Promise<PlatformSettingsRow> {
  const { data, error } = await supabase
    .from('platform_settings')
    .update({
      feedback_enabled: enabled,
      updated_at:       new Date().toISOString(),
      updated_by:       updatedBy,
    })
    .eq('id', 1)
    .select('id, anon_retention_days, anon_signups_per_ip_per_day, feedback_enabled, updated_at, updated_by')
    .single();
  if (error) {
    throw new Error(error.message || error.code || 'Update failed');
  }
  return data as PlatformSettingsRow;
}

/** Read the per-tenant feedback override (and the resolved platform
 *  default for display). NULL override = inherit. */
export async function fetchTenantFeedbackToggle(tenantId: string): Promise<{
  override:        boolean | null;
  platformDefault: boolean;
}> {
  const [{ data: brand, error: brandErr }, platform] = await Promise.all([
    supabase
      .from('branding_configs')
      .select('feedback_enabled_override')
      .eq('tenant_id', tenantId)
      .maybeSingle(),
    fetchPlatformSettings(),
  ]);
  if (brandErr) {
    throw new Error(brandErr.message || 'Could not read branding config');
  }
  return {
    override:        (brand as { feedback_enabled_override: boolean | null } | null)?.feedback_enabled_override ?? null,
    platformDefault: platform?.feedback_enabled ?? true,
  };
}

/** Set or clear the per-tenant feedback override. Pass null to inherit. */
export async function updateTenantFeedbackToggle(
  tenantId: string,
  override: boolean | null,
): Promise<void> {
  const { error } = await supabase
    .from('branding_configs')
    .update({ feedback_enabled_override: override })
    .eq('tenant_id', tenantId);
  if (error) {
    throw new Error(error.message || error.code || 'Update failed');
  }
}

// ── Feedback list (admin views) ──────────────────────────────────────────────

export interface FeedbackItem {
  id:           string;
  tenant_id:    string;
  user_id:      string | null;
  user_email:   string | null;
  user_name:    string | null;
  comment:      string;
  rating:       number | null;
  page_url:     string | null;
  status:       'new' | 'read' | 'archived';
  is_anonymous: boolean;
  created_at:   string;
  updated_at:   string;
}

export interface FeedbackListResult {
  items:  FeedbackItem[];
  count:  number;
  stats:  { new: number; read: number; archived: number };
}

/** Fetch feedback rows from the App DB via list-tenant-feedback.
 *  Omit tenantId for the super-admin "all tenants" view. */
export async function fetchTenantFeedback(args: {
  tenantId?: string;
  status?:   'new' | 'read' | 'archived' | 'all';
  limit?:    number;
  offset?:   number;
}): Promise<FeedbackListResult> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }
  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/list-tenant-feedback`, {
    method: 'POST',
    headers: {
      'Content-Type':       'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({
      tenant_id: args.tenantId,
      status:    args.status ?? 'all',
      limit:     args.limit ?? 50,
      offset:    args.offset ?? 0,
    }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || `list-tenant-feedback HTTP ${res.status}`);
  return {
    items: json.items ?? [],
    count: json.count ?? 0,
    stats: json.stats ?? { new: 0, read: 0, archived: 0 },
  };
}

/** Update a feedback row's status (mark read / archive / reopen). */
export async function updateFeedbackStatus(
  id:     string,
  action: 'mark_read' | 'archive' | 'reopen',
): Promise<void> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing App DB env vars');
  }
  const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/list-tenant-feedback`, {
    method: 'POST',
    headers: {
      'Content-Type':       'application/json',
      'x-provision-secret': PROVISION_SECRET,
    },
    body: JSON.stringify({ id, action }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || `update feedback status HTTP ${res.status}`);
}

/** Update the platform-wide per-IP signup cap. Super admin only (RLS). */
export async function updateAnonSignupsPerIpPerDay(
  value:     number,
  updatedBy: string | null,
): Promise<PlatformSettingsRow> {
  if (!Number.isInteger(value) || value < 1 || value > 1000) {
    throw new Error('anon_signups_per_ip_per_day must be an integer between 1 and 1000');
  }
  const { data, error } = await supabase
    .from('platform_settings')
    .update({
      anon_signups_per_ip_per_day: value,
      updated_at:                  new Date().toISOString(),
      updated_by:                  updatedBy,
    })
    .eq('id', 1)
    .select('id, anon_retention_days, anon_signups_per_ip_per_day, updated_at, updated_by')
    .single();
  if (error) {
    throw new Error(error.message || error.code || 'Update failed');
  }
  return data as PlatformSettingsRow;
}

/** Read the per-tenant guest-signup daily cap. Lives on branding_configs.
 *  Used by the tenant admin UI to display and edit the value. */
export async function fetchTenantGuestSignupCap(tenantId: string): Promise<number> {
  const { data, error } = await supabase
    .from('branding_configs')
    .select('max_guest_signups_per_day')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw error;
  return (data as { max_guest_signups_per_day: number | null } | null)?.max_guest_signups_per_day ?? 50;
}

/** Set the per-tenant guest-signup daily cap. Set to 0 to disable
 *  guest signups entirely on this tenant. */
export async function updateTenantGuestSignupCap(
  tenantId: string,
  value:    number,
): Promise<void> {
  if (!Number.isInteger(value) || value < 0 || value > 100_000) {
    throw new Error('max_guest_signups_per_day must be an integer between 0 and 100000');
  }
  const { error } = await supabase
    .from('branding_configs')
    .update({ max_guest_signups_per_day: value })
    .eq('tenant_id', tenantId);
  if (error) {
    throw new Error(error.message || error.code || 'Update failed');
  }
}

/** Read just the anon-retention override (and the resolved platform
 *  default for display) for one tenant. NULL override means "inherit
 *  the platform default". */
export async function fetchTenantAnonRetention(tenantId: string): Promise<{
  override:        number | null;
  platformDefault: number;
}> {
  const [{ data: brand, error: brandErr }, platform] = await Promise.all([
    supabase
      .from('branding_configs')
      .select('anon_retention_days_override')
      .eq('tenant_id', tenantId)
      .maybeSingle(),
    fetchPlatformSettings(),
  ]);
  if (brandErr) throw brandErr;
  return {
    override:        (brand as { anon_retention_days_override: number | null } | null)?.anon_retention_days_override ?? null,
    platformDefault: platform?.anon_retention_days ?? 30,
  };
}

/** Set or clear the tenant-specific anon-retention override. Pass null
 *  to revert to the platform default. 0 is allowed as a "purge any
 *  guest who isn't actively signed in right now" power-user value. */
export async function updateTenantAnonRetention(
  tenantId: string,
  override: number | null,
): Promise<void> {
  if (override !== null && (!Number.isInteger(override) || override < 0 || override > 3650)) {
    throw new Error('anon_retention_days_override must be an integer between 0 and 3650');
  }
  const { error } = await supabase
    .from('branding_configs')
    .update({ anon_retention_days_override: override })
    .eq('tenant_id', tenantId);
  if (error) {
    // Wrap PostgrestError as a real Error so callers don't see
    // "[object Object]" if they only check instanceof Error. Common
    // failure here: the BETWEEN-0-AND-3650 CHECK constraint hasn't
    // been migrated yet on this DB, so saving 0 still fails 400.
    throw new Error(error.message || error.code || 'Update failed');
  }
}

/** Update the platform-default anonymous-user retention window. Requires
 *  super admin — enforced by RLS via public.is_platform_admin(). 0
 *  is allowed as a "purge any guest who isn't actively signed in right
 *  now" power-user value. */
export async function updateAnonRetentionDefault(
  anonRetentionDays: number,
  updatedBy: string | null,
): Promise<PlatformSettingsRow> {
  if (!Number.isInteger(anonRetentionDays) || anonRetentionDays < 0 || anonRetentionDays > 3650) {
    throw new Error('anon_retention_days must be an integer between 0 and 3650');
  }
  const { data, error } = await supabase
    .from('platform_settings')
    .update({
      anon_retention_days: anonRetentionDays,
      updated_at:          new Date().toISOString(),
      updated_by:          updatedBy,
    })
    .eq('id', 1)
    .select('id, anon_retention_days, updated_at, updated_by')
    .single();
  if (error) {
    throw new Error(error.message || error.code || 'Update failed');
  }
  return data as PlatformSettingsRow;
}

/** Update the platform-default UTC hour the anonymous-user cleanup
 *  cron fires for tenants without their own override. 0..23. Super
 *  admin only — RLS enforces via public.is_platform_admin(). The
 *  cron job itself runs hourly and filters to tenants whose effective
 *  hour matches the current UTC hour. */
export async function updateAnonCleanupHourDefault(
  hourUtc:   number,
  updatedBy: string | null,
): Promise<PlatformSettingsRow> {
  if (!Number.isInteger(hourUtc) || hourUtc < 0 || hourUtc > 23) {
    throw new Error('anon_cleanup_hour_utc must be an integer between 0 and 23');
  }
  const { data, error } = await supabase
    .from('platform_settings')
    .update({
      anon_cleanup_hour_utc: hourUtc,
      updated_at:            new Date().toISOString(),
      updated_by:            updatedBy,
    })
    .eq('id', 1)
    .select('id, anon_retention_days, anon_signups_per_ip_per_day, feedback_enabled, anon_cleanup_hour_utc, updated_at, updated_by')
    .single();
  if (error) {
    throw new Error(error.message || error.code || 'Update failed');
  }
  return data as PlatformSettingsRow;
}

/** Read the tenant's per-tenant cleanup-hour override + the current
 *  platform default. Mirrors the shape of fetchTenantAnonRetention so
 *  the tenant-admin UI follows the same "override OR inherit" pattern.
 *  Returns 4 (04:00 UTC) defaults when columns aren't migrated yet. */
export async function fetchTenantAnonCleanupHour(tenantId: string): Promise<{
  override: number | null;
  platformDefault: number;
}> {
  const [brandRes, platform] = await Promise.all([
    supabase
      .from('branding_configs')
      .select('anon_cleanup_hour_utc_override')
      .eq('tenant_id', tenantId)
      .maybeSingle(),
    fetchPlatformSettings(),
  ]);

  // 42703 (undefined_column) means the migration hasn't applied yet —
  // surface a sensible default rather than blowing up the page.
  let override: number | null = null;
  if (brandRes.error) {
    if ((brandRes.error as { code?: string }).code !== '42703') {
      throw new Error(brandRes.error.message || brandRes.error.code || 'Failed to read tenant branding');
    }
  } else {
    override = (brandRes.data as { anon_cleanup_hour_utc_override: number | null } | null)?.anon_cleanup_hour_utc_override ?? null;
  }

  return {
    override,
    platformDefault: platform?.anon_cleanup_hour_utc ?? 4,
  };
}

/** Set or clear the tenant's cleanup-hour override. Pass null to revert
 *  to the platform default. Caller must be the tenant admin (or super
 *  admin) — RLS enforces via the branding_configs policy. */
export async function updateTenantAnonCleanupHour(
  tenantId: string,
  hourUtc:  number | null,
): Promise<void> {
  if (hourUtc !== null && (!Number.isInteger(hourUtc) || hourUtc < 0 || hourUtc > 23)) {
    throw new Error('anon_cleanup_hour_utc_override must be null or an integer between 0 and 23');
  }
  const { error } = await supabase
    .from('branding_configs')
    .update({ anon_cleanup_hour_utc_override: hourUtc })
    .eq('tenant_id', tenantId);
  if (error) {
    throw new Error(error.message || error.code || 'Update failed');
  }
}

// ── Anonymous-user cleanup (cross-DB: Platform reads policy, App DB deletes) ──
// The Platform UI knows the effective retention per tenant (from
// platform_settings + branding_configs overrides), so we resolve the
// policy here and hand the App DB a flat list to act on. The App DB
// edge function is the only one that touches auth.users, so deletion
// stays where the data is.

export interface AnonCleanupTenantResult {
  tenant_id:           string;
  anon_retention_days: number;
  candidates:          string[];
  deleted:             string[];
  errors:              { user_id: string; error: string }[];
  skipped?:            string;
}

export interface AnonCleanupResult {
  ok:               boolean;
  dryRun:           boolean;
  tenantsScanned:   number;
  totalCandidates:  number;
  totalDeleted:     number;
  /** Number of anon_signup_log rows pruned during this run (housekeeping
   *  for the rate-limit log, which only needs the last 24h of data). */
  prunedLogRows?:   number;
  perTenant:        AnonCleanupTenantResult[];
}

/** Resolve the effective retention (override → platform default → 30)
 *  for every active tenant, or for one specific tenant when scoped. */
async function resolveTenantPolicies(scope: { tenantId?: string }): Promise<
  { tenant_id: string; anon_retention_days: number }[]
> {
  // 1. Get the platform default once. Wrap any Supabase error so
  //    downstream catchers don't see `[object Object]`.
  let platformDefault = 30;
  try {
    const platform = await fetchPlatformSettings();
    platformDefault = platform?.anon_retention_days ?? 30;
  } catch (e) {
    const msg = (e as { message?: string })?.message || String(e);
    throw new Error(`Could not read platform retention default: ${msg}`);
  }

  // 2. Get the list of tenants we care about + their override (NULL = inherit).
  type Row = { id: string; status: string; branding_configs?: { anon_retention_days_override: number | null } | { anon_retention_days_override: number | null }[] | null };

  // When scoped to a specific tenant, read branding_configs directly —
  // tenant admins have RLS permission on their own branding row but
  // may not on the tenants table list, depending on policy. Going via
  // branding_configs sidesteps that and gives a clearer error path.
  if (scope.tenantId) {
    const { data, error } = await supabase
      .from('branding_configs')
      .select('tenant_id, anon_retention_days_override')
      .eq('tenant_id', scope.tenantId)
      .maybeSingle();
    if (error) {
      throw new Error(`Could not read tenant branding config: ${error.message || error.code || 'unknown error'}`);
    }
    const override = (data as { anon_retention_days_override: number | null } | null)?.anon_retention_days_override ?? null;
    return [{
      tenant_id:           scope.tenantId,
      anon_retention_days: override ?? platformDefault,
    }];
  }

  // Super-admin path: list every operational tenant + its branding row.
  // The tenant_status enum is ('active', 'trial', 'suspended', 'cancelled')
  // — no 'deleted'. `.neq('status', 'deleted')` returned a Postgres
  // 22P02 ("invalid input value for enum") because the comparison value
  // isn't a valid enum member. Allowlist the two operational states
  // instead; suspended/cancelled tenants are skipped from cleanup.
  const { data, error } = await supabase
    .from('tenants')
    .select('id, status, branding_configs(anon_retention_days_override)')
    .in('status', ['active', 'trial']);
  if (error) {
    throw new Error(`Could not list tenants: ${error.message || error.code || 'unknown error'}`);
  }

  return (data as Row[] || []).map(t => {
    // PostgREST returns the nested relation as object OR array depending
    // on FK arity. Normalize both.
    const brand = Array.isArray(t.branding_configs) ? t.branding_configs[0] : t.branding_configs;
    const override = brand?.anon_retention_days_override ?? null;
    return {
      tenant_id:           t.id,
      anon_retention_days: override ?? platformDefault,
    };
  });
}

/** Run the anonymous-user cleanup. When tenantId is omitted, runs for
 *  every active tenant — that's the super-admin use case. When set,
 *  runs only for that tenant (tenant-admin use case).
 *  Set dryRun=true to preview candidate user IDs without deleting. */
export async function runAnonCleanup(args: {
  tenantId?: string;
  dryRun:    boolean;
}): Promise<AnonCleanupResult> {
  if (!APP_SUPABASE_URL || !PROVISION_SECRET) {
    throw new Error('Missing VITE_APP_SUPABASE_URL or VITE_PROVISION_SECRET — cleanup cannot reach the App DB');
  }

  const tenantPolicies = await resolveTenantPolicies({ tenantId: args.tenantId });
  if (tenantPolicies.length === 0) {
    return { ok: true, dryRun: args.dryRun, tenantsScanned: 0, totalCandidates: 0, totalDeleted: 0, perTenant: [] };
  }

  let res: Response;
  try {
    res = await fetch(`${APP_SUPABASE_URL}/functions/v1/cleanup-anonymous-users`, {
      method: 'POST',
      headers: {
        'Content-Type':       'application/json',
        'x-provision-secret': PROVISION_SECRET,
      },
      body: JSON.stringify({ tenantPolicies, dryRun: args.dryRun }),
    });
  } catch (e) {
    // Network-level failure (DNS, CORS, server unreachable, etc.) —
    // surface the underlying message rather than letting a non-Error
    // object propagate to UI.
    throw new Error(`Could not reach the App DB cleanup function: ${(e as { message?: string })?.message || String(e)}`);
  }

  // The function returns JSON; fall back to text if something upstream
  // (504, proxy, etc.) returned HTML so the UI sees a useful message.
  let body: any = null;
  const ctype = res.headers.get('content-type') || '';
  if (ctype.includes('application/json')) {
    try { body = await res.json(); } catch { /* keep null */ }
  } else {
    try { body = await res.text(); } catch { /* keep null */ }
  }

  if (!res.ok) {
    const msg = (body && typeof body === 'object' && body.error)
      ? body.error
      : typeof body === 'string' && body
        ? body
        : `cleanup-anonymous-users HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body as AnonCleanupResult;
}

// ─────────────────────────────────────────────────────────────────────────────
// Infrastructure page — list/mutate helpers
//
// Wrap the two edge functions (infra-list, infra-mutate) that back the
// super-admin /admin/infrastructure page. The page lets a platform
// admin see what's scheduled via pg_cron on either DB, see which
// Vault + edge-function secrets exist, and create/rotate/unset Vault
// secrets without touching the SQL editor.
//
// All actions accept a `target: 'platform' | 'app'` so the same UI
// can manage both projects without two parallel API surfaces.
// ─────────────────────────────────────────────────────────────────────────────

export type InfraTarget = 'platform' | 'app';

/** One row of execution history for a single cron job, returned by
 *  list_job_runs. duration_ms is null while a job is still running
 *  (end_time hasn't been set yet) — UI should fall back to a
 *  "running" indicator in that case. return_message is truncated
 *  server-side to 500 chars. */
export interface InfraJobRunRow {
  runid:          string;
  jobid:          string;
  job_pid:        number | null;
  database:       string | null;
  username:       string | null;
  status:         string;             // 'succeeded' | 'failed' | 'starting' | 'running' (pg_cron docs)
  start_time:     string | null;
  end_time:       string | null;
  duration_ms:    number | null;
  return_message: string | null;
}

/** Row returned by list_scheduled_functions — one per pg_cron job
 *  whose command POSTs to /functions/v1/<edge-fn> via pg_net. */
export interface InfraScheduledFunctionRow {
  jobid:         string;
  jobname:       string | null;
  schedule:      string;          // cron expression, e.g. "* * * * *"
  command:       string;          // full SQL body of the cron command
  active:        boolean;
  function_name: string | null;   // parsed out of the command's URL
  last_run_at:   string | null;
  last_end_at:   string | null;
  last_status:   string | null;   // 'succeeded' | 'failed' | null
  last_message:  string | null;   // first 240 chars of return_message
}

/** Metadata for one Vault secret. Values are NEVER returned. */
export interface InfraVaultSecretRow {
  id:          string;
  name:        string;
  description: string | null;
  created_at:  string | null;
  updated_at:  string | null;
}

/** Edge-function secret name surfaced from Supabase Management API.
 *  Values are intentionally stripped before reaching the client. */
export interface InfraEdgeSecretRow {
  name:       string;
  updated_at: string | null;
}

interface InfraListResponse<T> {
  ok:          boolean;
  rows?:       T[];
  configured?: boolean;
  error?:      string;
}

/** Internal helper — invokes one of the infra functions and unwraps
 *  the FunctionsError → flat { ok, error } shape the UI expects. */
async function callInfra<T>(
  fnName: 'infra-list' | 'infra-mutate',
  body:   Record<string, unknown>,
): Promise<T & { ok: boolean; error?: string; configured?: boolean }> {
  const { data, error } = await supabase.functions.invoke(fnName, { body });
  if (error) {
    // The functions.invoke wrapper hides the response body when the
    // status is non-2xx. Try to surface it for "not configured" 503s
    // so the UI can render a helpful empty state instead of a generic
    // failure toast.
    const status   = (error as any)?.context?.status;
    const respBody = (error as any)?.context?.body;
    let parsed: any = null;
    if (respBody) {
      try { parsed = typeof respBody === 'string' ? JSON.parse(respBody) : respBody; } catch { /* ignore */ }
    }
    return {
      ok:         false,
      error:      parsed?.error || error.message || `${fnName} HTTP ${status ?? '???'}`,
      configured: parsed?.configured ?? (status === 503 ? false : undefined),
    } as any;
  }
  return { ok: true, ...(data as any) } as any;
}

export async function listScheduledFunctions(target: InfraTarget) {
  return callInfra<InfraListResponse<InfraScheduledFunctionRow>>('infra-list', {
    action: 'list_scheduled_functions',
    target,
  });
}

export async function listVaultSecrets(target: InfraTarget) {
  return callInfra<InfraListResponse<InfraVaultSecretRow>>('infra-list', {
    action: 'list_vault_secrets',
    target,
  });
}

export async function listEdgeSecrets(target: InfraTarget) {
  return callInfra<InfraListResponse<InfraEdgeSecretRow>>('infra-list', {
    action: 'list_edge_secrets',
    target,
  });
}

export async function toggleCronJob(target: InfraTarget, jobid: string, active: boolean) {
  return callInfra<{ row?: { jobname: string; active: boolean } }>('infra-mutate', {
    action: 'toggle_cron',
    target,
    jobid,
    active,
  });
}

/** Recent execution history for ONE cron job (Run History modal).
 *
 *  `limit` is clamped server-side to [1, 200]; the default of 50
 *  matches what the modal renders without needing pagination. */
export async function listJobRuns(target: InfraTarget, jobid: string, limit = 50) {
  return callInfra<InfraListResponse<InfraJobRunRow>>('infra-list', {
    action: 'list_job_runs',
    target,
    jobid,
    limit,
  });
}

/** Trigger a cron'd function NOW, bypassing its schedule. The edge
 *  function looks up the job's command body and EXECUTEs it on the
 *  target project — same semantics as a real cron tick, but doesn't
 *  show up in cron.job_run_details (instead we get a platform_audit_log
 *  row). `jobname` is purely for audit-log readability.
 *
 *  Returns ok=true the moment the underlying SQL dispatch completes;
 *  for net.http_post calls this means "request queued in pg_net," NOT
 *  "edge function responded 200." Surface a transient toast and let
 *  the operator check the History modal a few seconds later. */
export async function runCronNow(target: InfraTarget, jobid: string, jobname?: string | null) {
  return callInfra<{ jobid?: string }>('infra-mutate', {
    action: 'run_cron_now',
    target,
    jobid,
    jobname: jobname ?? null,
  });
}

export async function createVaultSecret(
  target: InfraTarget,
  name: string,
  value: string,
  description?: string,
) {
  return callInfra<{ id?: string }>('infra-mutate', {
    action: 'vault_create',
    target,
    name,
    value,
    description: description ?? '',
  });
}

export async function rotateVaultSecret(target: InfraTarget, name: string, value: string) {
  return callInfra<{}>('infra-mutate', {
    action: 'vault_rotate',
    target,
    name,
    value,
  });
}

export async function unsetVaultSecret(target: InfraTarget, name: string) {
  return callInfra<{}>('infra-mutate', {
    action: 'vault_unset',
    target,
    name,
  });
}

// ── Edge-function environment secrets (write) ──────────────────────────────
// Set (create or update) an edge-function secret value via the Management API.
// Supabase restarts the project's functions to pick up the new value.
export async function setEdgeSecret(target: InfraTarget, name: string, value: string) {
  return callInfra<{}>('infra-mutate', {
    action: 'edge_secret_set',
    target,
    name,
    value,
  });
}

export async function unsetEdgeSecret(target: InfraTarget, name: string) {
  return callInfra<{}>('infra-mutate', {
    action: 'edge_secret_unset',
    target,
    name,
  });
}

/** Recent rows from platform_audit_log — surfaced under the
 *  Infrastructure page as a "recent activity" feed. Reads via the
 *  regular client (RLS gates SELECT on platform_admins). */
export interface InfraAuditRow {
  id:             number;
  actor_user_id:  string;
  actor_email:    string;
  action:         string;
  target_project: 'platform' | 'app';
  target_name:    string;
  payload:        Record<string, unknown> | null;
  ok:             boolean;
  error_message:  string | null;
  created_at:     string;
}

/** Initiate a super-admin "Sign in as <user>" session.
 *
 *  Calls the Platform `start-impersonation` edge fn which:
 *   1. Verifies the caller is a platform_admin (server-side).
 *   2. Resolves the target tenant's domain.
 *   3. Cross-calls App `admin-impersonate-user` to mint a Supabase
 *      magiclink whose redirect lands on the App's /auth/impersonate
 *      route — that route swaps the session in and stamps the
 *      impersonation banner marker.
 *   4. Audits both sides (platform_audit_log + admin_audit_log).
 *
 *  The UI should open the returned `redirect_url` in a NEW TAB so the
 *  super-admin's own Platform session stays intact. */
export async function startImpersonation(params: {
  tenantId: string;
  userId:   string;
  reason?:  string;
  /** 'direct' (default) — land on /home with session active.
   *  'preview' — land on the tenant's anonymous splash with the session
   *  staged but not yet active. The operator clicks a floating CTA to
   *  consume the staged session and enter the app. Use 'preview' for
   *  debugging splash → auth flow bugs (broken OG images, misrouted
   *  CTAs, redirect loops). */
  mode?:    'direct' | 'preview';
}): Promise<{
  ok: boolean;
  redirect_url?: string;
  target_email?: string;
  expires_at?: string;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke('start-impersonation', {
    body: {
      target_tenant_id: params.tenantId,
      target_user_id:   params.userId,
      reason:           params.reason ?? '',
      mode:             params.mode ?? 'direct',
    },
  });
  if (error) {
    // functions.invoke hides the response body on non-2xx; try to
    // surface the underlying message for "forbidden" / "no resolvable
    // domain" so the modal can show something useful.
    const status   = (error as any)?.context?.status;
    const respBody = (error as any)?.context?.body;
    let parsed: any = null;
    if (respBody) {
      try { parsed = typeof respBody === 'string' ? JSON.parse(respBody) : respBody; } catch { /* ignore */ }
    }
    return { ok: false, error: parsed?.error || error.message || `start-impersonation HTTP ${status ?? '???'}` };
  }
  return { ok: true, ...(data as any) };
}

export async function fetchInfraAuditLog(limit = 50): Promise<InfraAuditRow[]> {
  const { data, error } = await supabase
    .from('platform_audit_log')
    .select('id, actor_user_id, actor_email, action, target_project, target_name, payload, ok, error_message, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as InfraAuditRow[];
}
